import { createHash, createHmac, timingSafeEqual } from "node:crypto";
import {
  approvalIdentifier,
  ApproveSlackUserLinkAccessRequest,
  DEFAULT_FIRST_PARTY_MCP_TOOLS,
  hasOpenGeniSlackReactionScope,
  ListSlackUserLinkAccessRequestsResponse,
  PrepareSlackUserLinkAccessRequest,
  evaluateSlackTaskPolicy,
  resolveWorkspaceSlackOrchestrationNoticeSettings,
  resolveWorkspaceSlackReactionSummonSettings,
  SlackChannelRouteListResponse,
  SlackReactionChannelListResponse,
  UpdateSlackChannelRoutesRequest,
  SlackUserLinkAccessMutationRequest,
  SlackUserLinkAccessRequest,
  type AccessGrant,
  type FileResourceRef,
  type FirstPartyMcpToolName,
  type HumanInputQuestion,
  type ResolvedWorkspaceSlackOrchestrationNoticeSettings,
  type ChildRequiresActionPayload,
  type SessionAuthorizationListScope,
  type SessionEvent,
  type WorkspaceSlackReactionSummonSettings,
  workspaceSlackReactionChannelAllowed,
} from "@opengeni/contracts";
import {
  allowedFirstPartyMcpToolsForSession,
  resolveFirstPartyMcpToolPolicy,
  type Settings,
} from "@opengeni/config";
import {
  acceptSessionHumanInputResponse,
  acceptSessionApprovalDecision,
  approveSlackUserLinkAccessRequest,
  advanceSlackInteractionDelivery,
  bindSlackInteractionSession,
  cancelSlackUserLinkAccessRequest,
  claimSlackAppHomeRefresh,
  claimSlackInteractionDelivery,
  claimSlackInteractionProgressDelivery,
  claimSlackInteractionInbox,
  closeSlackInteractionDelivery,
  completeSlackUserLinkAccessIfGranted,
  decodeSessionListCursor,
  deferSlackInteractionDelivery,
  deleteSlackBotUserLink,
  enqueueSlackAppHomeRefresh,
  enqueueSlackInteractionInbox,
  getConnectionMetadata,
  getOrCreateSlackInteraction,
  getLatestSessionModelForSubject,
  getSession,
  getSessionEvent,
  getSessionHumanInputRequest,
  childRequiresActionResolutionExists,
  getSessionSystemUpdateById,
  getSlackBotPostOperation,
  getSlackBotUserLink,
  getSlackInteractionActionHandle,
  getSlackInteractionByClientEventId,
  getSlackInteractionById,
  getSlackInteractionByConnectionRoute,
  probeSlackActionHandleTenancy,
  openSlackRoutePrompt,
  getSlackRoutePromptByOption,
  settleSlackRoutePrompt,
  answerSlackRoutePrompt,
  type SlackRoutableWorkspace,
  getSlackChannelRoute,
  listSlackChannelRoutes,
  upsertSlackChannelRoute,
  deleteSlackChannelRoute,
  getSlackUserDmRoute,
  listNamedSubjectSlackRoutableWorkspaces,
  getActiveSlackTaskPolicy,
  getSlackSharedTaskOrigin,
  getSessionEventByClientEventId,
  getWorkspace,
  getWorkspaceGrant,
  namedSubjectPersonalWorkspaceId,
  resolveSlackTargetAuthority,
  listSlackInteractionProgressDeliveryEvidence,
  listSessionEventPage,
  listSessionHumanInputRequests,
  listPendingSlackUserLinkAccessRequests,
  listSlackInstallationBindings,
  slackUserLinkAccessRequestTeam,
  rekeySlackInteractionRoute,
  reopenSlackInteractionDelivery,
  reserveSlackInteractionActionHandles,
  renewSlackAppHomeRefreshClaim,
  releaseSlackInteractionDelivery,
  releaseSlackAppHomeRefresh,
  releaseSlackInteractionInbox,
  requestSlackUserLinkWorkspaceAccess,
  resolveSlackInstallationRoute,
  resolveSlackInteractionFirstTaskHint,
  saveSlackInteractionInboxReactionCheckpoint,
  saveSlackSharedTaskOrigin,
  settleSlackInteractionInbox,
  settleSlackAppHomeRefresh,
  settleSlackInteractionActionHandles,
  listSessionsForSubject,
  SessionListAccessError,
  denySlackUserLinkAccessRequest,
  prepareSlackUserLinkAccessRequest,
  SlackUserLinkAccessPersistenceError,
  type SlackInstallationRoute,
  type SlackAppHomeRefresh,
  type SlackInteraction,
  type SlackInteractionActionHandle,
  type SlackInteractionActionKind,
  type SlackInteractionInboxEntry,
  type SlackInteractionTriggerKind,
} from "@opengeni/db";
import {
  acceptSessionUserMessage,
  controlHumanSessionWorkstream,
  createSessionForRequest,
  hasPermission,
  requireAccessContext,
  requireAccessGrant,
  requireSessionAuthorizationListScope,
  resolveWorkspaceCatalogSettings,
  type ApiRouteDeps,
} from "@opengeni/core";
import { publishDurableSessionEvents } from "@opengeni/events";
import type { Context, Hono } from "hono";
import { HTTPException } from "hono/http-exception";
import {
  createOpenGeniSlackBotInteractionClient,
  SLACK_REACTION_IMAGE_MAX_BYTES,
  type OpenGeniSlackBotClient,
  type SlackMessageBlock,
  SlackBotProviderError,
} from "./slack-bot";
import {
  isSlackDirectMessageConversation,
  resolveSlackWorkspaceRoute,
  slackRoutedRequestText,
  type SlackRouteResolution,
  type SlackRouteTenancy,
} from "./slack-routing";
import {
  buildSlackAppHomeAccessBlocks,
  buildSlackAppHomeBlocks,
  escapeSlackMrkdwn,
  isSlackAppHomeLinkAction,
  slackAppHomeOpenedEvent,
} from "./slack-app-home";
import { importSlackReactionImage, type ImportedSlackReactionImage } from "../slack-reaction-files";

export const SLACK_INTERACTION_MAX_BODY_BYTES = 256 * 1024;
export const SLACK_SIGNATURE_REPLAY_WINDOW_SECONDS = 300;
export const SLACK_DELIVERY_EVENT_TYPES = [
  "agent.message.completed",
  "session.humanInput.requested",
  "session.requiresAction",
  "turn.completed",
  "turn.failed",
  "turn.cancelled",
  "session.status.changed",
  // Orchestration surfacing. Both are filtered again inside the pump: the
  // workspace must have opted in (both notices default off), and then only a
  // `child_requires_action` notice and a `limits` / `max_auto_continuations`
  // goal pause reach Slack, so the thread stays quiet for the deferred child
  // lifecycle kinds and for human/API/agent pauses the human already made.
  "system.update.pending",
  "goal.paused",
] as const;

const MAX_SLACK_TEXT_CHARS = 3_500;
const MAX_SLACK_INPUT_CHARS = 8_000;
const MAX_SLACK_INVOCATION_CONTEXT_MESSAGES = 15;
const MAX_SLACK_CHANNEL_CONTEXT_MESSAGES = 5;
const MAX_SLACK_REACTION_CONTEXT_MESSAGES = 15;
const MAX_SLACK_REACTION_FILE_SUMMARY_CHARS = 1_500;
const MAX_SLACK_REACTION_IMAGES = 4;
const MAX_SLACK_REACTION_IMAGE_AGGREGATE_BYTES = 16 * 1024 * 1024;
const MAX_PROGRESS_MESSAGES = 3;
const SLACK_USER_LINK_TTL_MS = 15 * 60_000;
const INBOX_LEASE_MS = 30_000;
const APP_HOME_LEASE_MS = 300_000;
const DELIVERY_LEASE_MS = 30_000;
const MAX_DELIVERY_ATTEMPTS = 8;
const MAX_DELIVERY_RETRY_MS = 5 * 60_000;
const SLACK_INTERACTION_BOT_SUBJECT_ID = "service:slack-interaction";
const SLACK_ACTION_TTL_MS = 7 * 24 * 60 * 60_000;
const MAX_SLACK_APPROVALS_PER_CARD = 8;
const MAX_SLACK_ACTIONS_PER_CARD = 20;
/** Blocked-worker cards are pointers, so the question preview stays short. */
const MAX_SLACK_CHILD_DETAIL_CHARS = 240;
const SLACK_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
/**
 * Goal pause reasons the Slack thread announces. `user_pause`, `api`, `agent`,
 * and `no_progress` are deliberately absent.
 *
 * A Map, not a plain object: this lookup is the sole gate of a two-reason
 * invariant, and a plain object indexed by a payload-derived string answers
 * `constructor` (and every other prototype key) with a truthy value that would
 * sail past the `if (!headline)` guard. No payload reaches it with such a
 * reason today; the gate should not depend on that staying true.
 */
const SLACK_GOAL_PAUSED_HEADLINES = new Map<string, string>([
  ["limits", "Goal paused (budget)"],
  ["max_auto_continuations", "Goal paused (continuation cap)"],
]);
/**
 * Slack delivery restrictions are durable session-level authority, not
 * attacker-adjacent user-message context. Migration 0240 backfills this exact
 * policy onto every pre-cutover session reserved by a Slack interaction.
 */
export const SLACK_SESSION_INSTRUCTIONS = [
  "This session is an OpenGeni Slack task surface. Treat Slack message and thread context as task-local unless a separate explicit authorized user action says otherwise.",
  "Execute direct, safe, sufficiently specified requests immediately.",
  "Ask one concise clarifying question only when materially required information is missing or the requested action is risky, irreversible, or authorization-sensitive.",
  "Do not write Slack context to Documents, Knowledge, Memory, preferences, Workspace Charter, instructions, or policy unless a separate explicit authorized user action requests it.",
  "Never expose private reasoning, credentials, secrets, raw logs, or unbounded output.",
  "Keep user-visible output concise, bounded, and safe to send back to Slack.",
].join(" ");

/**
 * Slack-originated tasks may retrieve the workspace bot's bounded read surface
 * on demand. Connector tools are explicit-only, so freeze that narrow context
 * selection at session creation while keeping Slack mutations out of the model
 * surface; interaction delivery remains owned by the durable delivery pump.
 */
export const SLACK_TASK_FIRST_PARTY_MCP_TOOLS = [
  ...DEFAULT_FIRST_PARTY_MCP_TOOLS,
  "slack_bot_list_channels",
  "slack_bot_channel_history",
  "slack_bot_thread_replies",
  "slack_bot_list_users",
  "slack_bot_list_files",
  "slack_bot_file_info",
  "slack_bot_file_content",
] satisfies readonly FirstPartyMcpToolName[];

function slackTaskFirstPartyMcpTools(settings: Settings): FirstPartyMcpToolName[] {
  const policy = resolveFirstPartyMcpToolPolicy(settings);
  const connectorTools = SLACK_TASK_FIRST_PARTY_MCP_TOOLS.filter(
    (tool) => !DEFAULT_FIRST_PARTY_MCP_TOOLS.includes(tool),
  );
  return allowedFirstPartyMcpToolsForSession(settings, [...policy.default, ...connectorTools]);
}

export type NormalizedSlackInteraction = {
  providerEventId: string;
  providerMessageId: string;
  slackTeamId: string;
  slackUserId: string;
  slackChannelId: string;
  slackMessageTs: string;
  slackThreadTs: string | null;
  triggerKind: SlackInteractionTriggerKind;
  text: string;
  hasFiles: boolean;
};

export function slackInteractionRoutePolicy(
  entry: Pick<
    SlackInteractionInboxEntry,
    "triggerKind" | "slackChannelId" | "slackThreadTs" | "slackMessageTs" | "slackUserId"
  >,
  options: { privateHandoff?: boolean } = {},
) {
  const directMessageShortcut = isDirectMessageShortcut(entry) || options.privateHandoff === true;
  const source = slackRouteKey(entry.slackChannelId, entry.slackThreadTs ?? entry.slackMessageTs);
  return {
    directMessageShortcut,
    requiresChannelAccess: !directMessageShortcut,
    visibility:
      entry.triggerKind === "dm" || directMessageShortcut
        ? ("private" as const)
        : ("workspace" as const),
    // A human-to-human DM may be shared by multiple linked workspace users. The
    // signed shortcut authorizes only the invoking user, so the pre-ack route
    // must keep each user's private reservation distinct until it is rekeyed to
    // that user's OpenGeni bot-DM thread.
    initialRouteKey: directMessageShortcut
      ? `${source}:shortcut-user:${entry.slackUserId}`
      : source,
  };
}

export function verifySlackRequestSignature(
  input: {
    timestamp: string | null;
    signature: string | null;
    rawBody: string;
  },
  signingSecret: string,
  nowMs = Date.now(),
): boolean {
  if (
    !/^\d{1,16}$/.test(input.timestamp ?? "") ||
    !/^v0=[0-9a-f]{64}$/.test(input.signature ?? "")
  ) {
    return false;
  }
  const timestamp = Number(input.timestamp);
  if (!Number.isSafeInteger(timestamp)) return false;
  const nowSeconds = Math.floor(nowMs / 1000);
  if (Math.abs(nowSeconds - timestamp) > SLACK_SIGNATURE_REPLAY_WINDOW_SECONDS) return false;
  const expected = `v0=${createHmac("sha256", signingSecret)
    .update(`v0:${input.timestamp}:${input.rawBody}`)
    .digest("hex")}`;
  const actualBytes = Buffer.from(input.signature!, "utf8");
  const expectedBytes = Buffer.from(expected, "utf8");
  return actualBytes.length === expectedBytes.length && timingSafeEqual(actualBytes, expectedBytes);
}

export function slackEventInboxEntry(
  payload: unknown,
  bot: Pick<SlackInstallationRoute, "botId" | "botUserId">,
): NormalizedSlackInteraction | null {
  const envelope = record(payload);
  if (!envelope || envelope.type !== "event_callback") return null;
  const event = record(envelope.event);
  const teamId = boundedString(envelope.team_id, 64);
  const eventId = boundedString(envelope.event_id, 256);
  if (!event || !teamId || !eventId) return null;
  if (event.bot_id || event.bot_profile || event.subtype || event.user === bot.botUserId)
    return null;
  const userId = boundedString(event.user, 64);
  const channelId = boundedString(event.channel, 64);
  const timestamp = boundedString(event.ts, 64);
  const threadTimestamp = boundedString(event.thread_ts, 64);
  const hasFiles =
    Array.isArray(event.files) && event.files.some((file) => boundedString(record(file)?.id, 64));
  const text = boundedText(event.text) ?? (hasFiles ? "(file-only Slack invocation)" : null);
  if (!userId || !channelId || !timestamp || !text) return null;
  let triggerKind: SlackInteractionTriggerKind;
  const explicitlyMentionsBot = text.includes(`<@${bot.botUserId}>`);
  if (
    event.type === "app_mention" ||
    (event.type === "message" && threadTimestamp && explicitlyMentionsBot)
  ) {
    // A mention is always an explicit invocation. In particular, a mention in
    // an otherwise-unmapped existing thread adopts that thread as the new
    // OpenGeni session surface; only ordinary message replies require a
    // pre-existing route.
    triggerKind = "app_mention";
  } else if (event.type === "message" && threadTimestamp) {
    triggerKind = "thread_reply";
  } else if (event.type === "message" && event.channel_type === "im") {
    triggerKind = "dm";
  } else {
    return null;
  }
  return {
    providerEventId: eventId,
    providerMessageId: `${teamId}:${channelId}:${timestamp}`,
    slackTeamId: teamId,
    slackUserId: userId,
    slackChannelId: channelId,
    slackMessageTs: timestamp,
    slackThreadTs: threadTimestamp,
    triggerKind,
    text,
    hasFiles,
  };
}

export function slackReactionInboxEntry(
  payload: unknown,
  bot: Pick<SlackInstallationRoute, "botUserId">,
  settings: WorkspaceSlackReactionSummonSettings,
): NormalizedSlackInteraction | null {
  const envelope = record(payload);
  if (!envelope || envelope.type !== "event_callback") return null;
  const event = record(envelope.event);
  const item = record(event?.item);
  const teamId = boundedString(envelope.team_id, 64);
  const eventId = boundedString(envelope.event_id, 256);
  const userId = boundedString(event?.user, 64);
  const reaction = boundedString(event?.reaction, 64);
  const channelId = boundedString(item?.channel, 64);
  const timestamp = boundedString(item?.ts, 64);
  if (
    !settings.enabled ||
    !event ||
    event.type !== "reaction_added" ||
    item?.type !== "message" ||
    !teamId ||
    !eventId ||
    !userId ||
    userId === bot.botUserId ||
    !reaction ||
    reaction !== settings.emoji ||
    !channelId ||
    !workspaceSlackReactionChannelAllowed(settings, channelId) ||
    !timestamp
  ) {
    return null;
  }
  const stableReactionIdentity = createHash("sha256")
    .update([teamId, userId, channelId, timestamp, reaction].join("\n"))
    .digest("hex");
  return {
    providerEventId: eventId,
    providerMessageId: `reaction:${stableReactionIdentity}`,
    slackTeamId: teamId,
    slackUserId: userId,
    slackChannelId: channelId,
    slackMessageTs: timestamp,
    slackThreadTs: null,
    triggerKind: "reaction",
    // Store only the exact emoji name before authorization/content fetch. The
    // provider message and bounded thread are projected at durable claim time.
    text: reaction,
    hasFiles: false,
  };
}

/**
 * Prove a Slack link token may name this workspace.
 *
 * Before routing, a token could only ever name the installation's own
 * workspace, and both intent routes asserted exactly that. A routed
 * conversation asks for access to the workspace the work would actually land
 * in, which is a different one, so the assertion becomes: the installation
 * binding resolves for the token's team, it is the same connection the token
 * was minted against, and the named workspace belongs to that installation's
 * organization.
 *
 * That is the same fence the route tables carry. It does not widen access by
 * itself: this only decides which workspace an access REQUEST may be raised
 * for, and the request still goes through the ordinary approval path.
 */
async function resolveSlackLinkTargetInstallation(
  deps: ApiRouteDeps,
  link: { slackTeamId: string; connectionId: string },
  workspaceId: string,
): Promise<{
  accountId: string;
  connectionId: string;
  workspaceName: string;
  /** Where the identity link lives, which is not the routed workspace. */
  identityHome: { accountId: string; workspaceId: string };
} | null> {
  const route = await resolveSlackInstallationRoute(deps.db, link.slackTeamId);
  if (!route || route.connectionId !== link.connectionId || route.accountId.length === 0) {
    return null;
  }
  const workspace = await getWorkspace(deps.db, workspaceId);
  if (!workspace || workspace.accountId !== route.accountId) return null;
  return {
    accountId: route.accountId,
    connectionId: route.connectionId,
    workspaceName: workspace.name,
    identityHome: { accountId: route.accountId, workspaceId: route.workspaceId },
  };
}

export function registerSlackInteractionRoutes(app: Hono, deps: ApiRouteDeps): void {
  app.post("/v1/integrations/slack/events", async (c) => {
    const signed = await readSignedSlackRequest(c, deps);
    const payload = parseJsonObject(signed.rawBody);
    if (payload.type === "url_verification") {
      const challenge = boundedString(payload.challenge, 512);
      if (!challenge) throw new HTTPException(400, { message: "invalid Slack challenge" });
      return c.json({ challenge });
    }
    const teamId = boundedString(payload.team_id, 64);
    if (!teamId) throw new HTTPException(400, { message: "invalid Slack event" });
    const installation = await resolveSlackInstallationRoute(deps.db, teamId);
    if (!installation)
      throw new HTTPException(403, {
        message: "Slack installation unavailable",
      });
    const event = record(payload.event);
    const appHomeEvent = slackAppHomeOpenedEvent(payload);
    if (appHomeEvent) {
      await enqueueSlackAppHomeRefresh(deps.db, {
        accountId: installation.accountId,
        workspaceId: installation.workspaceId,
        connectionId: installation.connectionId,
        slackTeamId: appHomeEvent.slackTeamId,
        slackUserId: appHomeEvent.slackUserId,
        providerEventId: appHomeEvent.eventId,
        providerViewHash: appHomeEvent.viewHash,
      });
      return c.json({ ok: true });
    }
    if (event?.type === "reaction_added") {
      const [workspace, connection] = await Promise.all([
        getWorkspace(deps.db, installation.workspaceId),
        getConnectionMetadata(deps.db, installation.workspaceId, installation.connectionId, null),
      ]);
      if (!workspace || !connection || !hasOpenGeniSlackReactionScope(connection.grantedScopes)) {
        return c.json({ ok: true });
      }
      const reactionEntry = slackReactionInboxEntry(
        payload,
        installation,
        resolveWorkspaceSlackReactionSummonSettings(workspace.settings),
      );
      if (reactionEntry) {
        await enqueueNormalizedSlackInteraction(deps, installation, reactionEntry);
      }
      return c.json({ ok: true });
    }
    const entry = slackEventInboxEntry(payload, installation);
    if (entry) await enqueueNormalizedSlackInteraction(deps, installation, entry);
    return c.json({ ok: true });
  });

  app.post("/v1/integrations/slack/commands", async (c) => {
    const signed = await readSignedSlackRequest(c, deps);
    const form = new URLSearchParams(signed.rawBody);
    if (form.get("command") !== deps.settings.slackCommand) {
      throw new HTTPException(400, { message: "invalid Slack command" });
    }
    const entry = normalizedFormInteraction(form, "slash_command");
    const installation = await resolveSlackInstallationRoute(deps.db, entry.slackTeamId);
    if (!installation)
      throw new HTTPException(403, {
        message: "Slack installation unavailable",
      });
    // `<command> info` is a read-only ephemeral explainer. It never reaches the
    // durable inbox, never verifies channel membership, and never creates a
    // session, so it is safe anywhere the command is available.
    if (isSlackInfoCommand(entry.text)) {
      return c.json(await slackInfoCommandResponse(deps, installation, entry));
    }
    const client = await createOpenGeniSlackBotInteractionClient(deps, {
      accountId: installation.accountId,
      workspaceId: installation.workspaceId,
      connectionId: installation.connectionId,
      subjectId: "service:slack-interaction",
    });
    try {
      await client.verifyChannelAccess(entry.slackChannelId);
    } catch (error) {
      if (error instanceof SlackBotProviderError && error.code === "not_in_channel") {
        return c.text(
          "OpenGeni is not a member of this channel. Add @OpenGeni, then run /opengeni again.",
          200,
        );
      }
      // Slash commands are not replayed through the Events API. A transient
      // membership preflight failure must therefore fall through to the
      // durable inbox, whose normal claim/backoff path retries the same task.
      // Permanent provider/local failures remain an honest request failure.
      if (!(error instanceof SlackBotProviderError) || permanentSlackDeliveryError(error)) {
        throw error;
      }
    }
    await enqueueNormalizedSlackInteraction(deps, installation, entry);
    return c.text("OpenGeni accepted this task and will reply in a thread.", 200);
  });

  app.post("/v1/integrations/slack/interactions", async (c) => {
    const signed = await readSignedSlackRequest(c, deps);
    const form = new URLSearchParams(signed.rawBody);
    const payload = parseJsonObject(form.get("payload") ?? "");
    if (payload.type === "block_actions") {
      if (isSlackAppHomeLinkAction(payload)) return c.json({ ok: true });
      const entry = normalizedBlockActionInteraction(payload);
      const installation = await resolveSlackInstallationRoute(deps.db, entry.slackTeamId);
      if (!installation) {
        throw new HTTPException(403, { message: "Slack installation unavailable" });
      }
      await enqueueNormalizedSlackInteraction(deps, installation, entry);
      return c.json({ ok: true });
    }
    if (payload.type !== "message_action") {
      throw new HTTPException(400, {
        message: "unsupported Slack interaction",
      });
    }
    const team = record(payload.team);
    const user = record(payload.user);
    const channel = record(payload.channel);
    const message = record(payload.message);
    const triggerId = boundedString(payload.trigger_id, 256);
    const teamId = boundedString(team?.id, 64);
    const userId = boundedString(user?.id, 64);
    const channelId = boundedString(channel?.id, 64);
    const messageTs = boundedString(message?.ts, 64);
    const threadTs = boundedString(message?.thread_ts, 64);
    const text = boundedText(message?.text);
    if (!triggerId || !teamId || !userId || !channelId || !messageTs || !text) {
      throw new HTTPException(400, {
        message: "invalid Slack message shortcut",
      });
    }
    const installation = await resolveSlackInstallationRoute(deps.db, teamId);
    if (!installation)
      throw new HTTPException(403, {
        message: "Slack installation unavailable",
      });
    await enqueueNormalizedSlackInteraction(deps, installation, {
      providerEventId: `shortcut:${triggerId}`,
      providerMessageId: `shortcut:${triggerId}`,
      slackTeamId: teamId,
      slackUserId: userId,
      slackChannelId: channelId,
      slackMessageTs: messageTs,
      slackThreadTs: threadTs,
      triggerKind: "message_shortcut",
      text,
      hasFiles: false,
    });
    return c.json({ ok: true });
  });

  app.post("/v1/workspaces/:workspaceId/integrations/slack/user-link-intents", async (c) => {
    const workspaceId = c.req.param("workspaceId");
    const context = await requireManagedSlackLinkHuman(c, deps);
    const payload = PrepareSlackUserLinkAccessRequest.parse(await c.req.json());
    const signingSecret = deps.settings.slackSigningSecret;
    const link = signingSecret ? verifySlackUserLinkToken(signingSecret, payload.linkToken) : null;
    if (!link || link.workspaceId !== workspaceId) {
      throw freshSlackLinkRequired();
    }
    const installation = await resolveSlackLinkTargetInstallation(deps, link, workspaceId);
    if (!installation) {
      throw freshSlackLinkRequired();
    }
    try {
      const prepared = await prepareSlackUserLinkAccessRequest(deps.db, {
        accountId: installation.accountId,
        workspaceId,
        tokenDigest: createHash("sha256").update(payload.linkToken).digest("hex"),
        connectionId: link.connectionId,
        slackTeamId: link.slackTeamId,
        slackUserId: link.slackUserId,
        subjectId: context.subjectId,
        subjectLabel: boundedString(context.subjectLabel, 512),
        expiresAt: new Date(link.expiresAt),
      });
      const completed = await completeSlackUserLinkAccessIfGranted(deps.db, {
        workspaceId,
        requestId: prepared.id,
        subjectId: context.subjectId,
        identityHome: installation.identityHome,
      });
      if (!completed) throw freshSlackLinkRequired();
      return c.json(
        SlackUserLinkAccessRequest.parse({
          ...completed,
          workspaceDisplayName: installation.workspaceName,
        }),
        201,
      );
    } catch (error) {
      throw slackLinkAccessHttpError(error);
    }
  });

  app.get(
    "/v1/workspaces/:workspaceId/integrations/slack/user-link-intents/:requestId",
    async (c) => {
      const workspaceId = c.req.param("workspaceId");
      const context = await requireManagedSlackLinkHuman(c, deps);
      const requestId = c.req.param("requestId");
      try {
        const current = await completeSlackUserLinkAccessIfGranted(deps.db, {
          workspaceId,
          requestId,
          subjectId: context.subjectId,
        });
        if (!current) throw freshSlackLinkRequired();
        const workspace = await getWorkspace(deps.db, workspaceId);
        return c.json(
          SlackUserLinkAccessRequest.parse({
            ...current,
            workspaceDisplayName: workspace?.name ?? null,
          }),
        );
      } catch (error) {
        throw slackLinkAccessHttpError(error);
      }
    },
  );

  app.post(
    "/v1/workspaces/:workspaceId/integrations/slack/user-link-intents/:requestId/request-access",
    async (c) => {
      const workspaceId = c.req.param("workspaceId");
      const context = await requireManagedSlackLinkHuman(c, deps);
      const payload = SlackUserLinkAccessMutationRequest.parse(await c.req.json());
      try {
        const request = await requestSlackUserLinkWorkspaceAccess(deps.db, {
          workspaceId,
          requestId: c.req.param("requestId"),
          actorSubjectId: context.subjectId,
          ...payload,
        });
        const workspace = await getWorkspace(deps.db, workspaceId);
        return c.json(
          SlackUserLinkAccessRequest.parse({
            ...request,
            workspaceDisplayName: workspace?.name ?? null,
          }),
        );
      } catch (error) {
        throw slackLinkAccessHttpError(error);
      }
    },
  );

  app.post(
    "/v1/workspaces/:workspaceId/integrations/slack/user-link-intents/:requestId/cancel",
    async (c) => {
      const workspaceId = c.req.param("workspaceId");
      const context = await requireManagedSlackLinkHuman(c, deps);
      const payload = SlackUserLinkAccessMutationRequest.parse(await c.req.json());
      try {
        const request = await cancelSlackUserLinkAccessRequest(deps.db, {
          workspaceId,
          requestId: c.req.param("requestId"),
          actorSubjectId: context.subjectId,
          ...payload,
        });
        const workspace = await getWorkspace(deps.db, workspaceId);
        return c.json(
          SlackUserLinkAccessRequest.parse({
            ...request,
            workspaceDisplayName: workspace?.name ?? null,
          }),
        );
      } catch (error) {
        throw slackLinkAccessHttpError(error);
      }
    },
  );

  app.get("/v1/workspaces/:workspaceId/members/access-requests/slack", async (c) => {
    const workspaceId = c.req.param("workspaceId");
    await requireAccessGrant(c, deps, workspaceId, "members:manage");
    const workspace = await getWorkspace(deps.db, workspaceId);
    const requests = await listPendingSlackUserLinkAccessRequests(deps.db, workspaceId);
    return c.json(
      ListSlackUserLinkAccessRequestsResponse.parse({
        requests: requests.map((request) => ({
          ...request,
          workspaceDisplayName: workspace?.name ?? null,
        })),
      }),
    );
  });

  app.post(
    "/v1/workspaces/:workspaceId/members/access-requests/slack/:requestId/approve",
    async (c) => {
      const workspaceId = c.req.param("workspaceId");
      const grant = await requireAccessGrant(c, deps, workspaceId, "members:manage");
      const payload = ApproveSlackUserLinkAccessRequest.parse(await c.req.json());
      // The identity link belongs to the installation, which for a routed
      // request is a different workspace from the one being granted. The
      // request row names its Slack team, so the binding resolves from it.
      const requestId = c.req.param("requestId");
      const requestTeam = await slackUserLinkAccessRequestTeam(deps.db, {
        workspaceId,
        requestId,
      });
      const installation = requestTeam
        ? await resolveSlackInstallationRoute(deps.db, requestTeam.slackTeamId)
        : null;
      try {
        const request = await approveSlackUserLinkAccessRequest(deps.db, {
          workspaceId,
          requestId,
          actorSubjectId: grant.subjectId,
          expectedVersion: payload.expectedVersion,
          idempotencyKey: payload.idempotencyKey,
          permissions: payload.permissions,
          ...(installation
            ? {
                identityHome: {
                  accountId: installation.accountId,
                  workspaceId: installation.workspaceId,
                },
              }
            : {}),
          ...(payload.role !== undefined ? { role: payload.role } : {}),
        });
        const workspace = await getWorkspace(deps.db, workspaceId);
        return c.json(
          SlackUserLinkAccessRequest.parse({
            ...request,
            workspaceDisplayName: workspace?.name ?? null,
          }),
        );
      } catch (error) {
        throw slackLinkAccessHttpError(error);
      }
    },
  );

  app.post(
    "/v1/workspaces/:workspaceId/members/access-requests/slack/:requestId/deny",
    async (c) => {
      const workspaceId = c.req.param("workspaceId");
      const grant = await requireAccessGrant(c, deps, workspaceId, "members:manage");
      const payload = SlackUserLinkAccessMutationRequest.parse(await c.req.json());
      try {
        const request = await denySlackUserLinkAccessRequest(deps.db, {
          workspaceId,
          requestId: c.req.param("requestId"),
          actorSubjectId: grant.subjectId,
          ...payload,
        });
        const workspace = await getWorkspace(deps.db, workspaceId);
        return c.json(
          SlackUserLinkAccessRequest.parse({
            ...request,
            workspaceDisplayName: workspace?.name ?? null,
          }),
        );
      } catch (error) {
        throw slackLinkAccessHttpError(error);
      }
    },
  );

  app.post("/v1/workspaces/:workspaceId/integrations/slack/user-links", async (c) => {
    const workspaceId = c.req.param("workspaceId");
    const grant = await requireAccessGrant(c, deps, workspaceId, "sessions:create");
    const body = record(await c.req.json().catch(() => null));
    const linkToken = boundedString(body?.linkToken, 2_048);
    const signingSecret = deps.settings.slackSigningSecret;
    if (!linkToken || !signingSecret) {
      throw new HTTPException(400, {
        message: "invalid or expired Slack identity link",
      });
    }
    const link = verifySlackUserLinkToken(signingSecret, linkToken);
    if (!link || link.workspaceId !== workspaceId) {
      throw new HTTPException(400, {
        message: "invalid or expired Slack identity link",
      });
    }
    const installation = await resolveSlackLinkTargetInstallation(deps, link, workspaceId);
    if (!installation) {
      throw new HTTPException(404, {
        message: "Slack installation not found",
      });
    }
    try {
      const prepared = await prepareSlackUserLinkAccessRequest(deps.db, {
        accountId: grant.accountId,
        workspaceId,
        tokenDigest: createHash("sha256").update(linkToken).digest("hex"),
        connectionId: link.connectionId,
        slackTeamId: link.slackTeamId,
        slackUserId: link.slackUserId,
        subjectId: grant.subjectId,
        subjectLabel: boundedString(grant.subjectLabel, 512),
        expiresAt: new Date(link.expiresAt),
      });
      const completed = await completeSlackUserLinkAccessIfGranted(deps.db, {
        workspaceId,
        requestId: prepared.id,
        subjectId: grant.subjectId,
        identityHome: installation.identityHome,
      });
      if (completed?.status !== "completed") throw freshSlackLinkRequired();
      const saved = await getSlackBotUserLink(
        deps.db,
        workspaceId,
        link.connectionId,
        link.slackUserId,
      );
      if (!saved || saved.subjectId !== grant.subjectId) throw freshSlackLinkRequired();
      return c.json(saved, 201);
    } catch (error) {
      throw slackLinkAccessHttpError(error);
    }
  });

  app.delete(
    "/v1/workspaces/:workspaceId/integrations/slack/user-links/:slackUserId",
    async (c) => {
      const workspaceId = c.req.param("workspaceId");
      await requireAccessGrant(c, deps, workspaceId, "connections:write");
      const connectionId = boundedString(c.req.query("connectionId"), 64);
      if (!connectionId) throw new HTTPException(400, { message: "connectionId is required" });
      return c.json({
        deleted: await deleteSlackBotUserLink(
          deps.db,
          workspaceId,
          connectionId,
          c.req.param("slackUserId"),
        ),
      });
    },
  );

  app.get("/v1/workspaces/:workspaceId/integrations/slack/channel-routes", async (c) => {
    const workspaceId = c.req.param("workspaceId");
    // Same gate as the reaction-channel allowlist beside it. The response names
    // workspaces across the organization, including ones the reader may hold no
    // grant in, so it is not an ordinary connections read.
    const grant = await requireAccessGrant(c, deps, workspaceId, "workspace:admin");
    const connectionId = boundedString(c.req.query("connectionId"), 64);
    if (!connectionId) throw new HTTPException(400, { message: "connectionId is required" });
    const routes = await listSlackChannelRoutes(
      deps.db,
      { accountId: grant.accountId, workspaceId },
      { connectionId },
    );
    // Names are resolved once per distinct target rather than per route.
    const names = new Map<string, string | null>();
    for (const route of routes) {
      if (names.has(route.targetWorkspaceId)) continue;
      names.set(
        route.targetWorkspaceId,
        (await getWorkspace(deps.db, route.targetWorkspaceId))?.name ?? null,
      );
    }
    return c.json(
      SlackChannelRouteListResponse.parse({
        routingEnabled: deps.settings.slackWorkspaceRoutingEnabled,
        routes: routes.map((route) => ({
          slackChannelId: route.slackChannelId,
          targetWorkspaceId: route.targetWorkspaceId,
          targetWorkspaceName: names.get(route.targetWorkspaceId) ?? null,
          source: route.source,
          updatedAt: route.updatedAt.toISOString(),
        })),
      }),
    );
  });

  app.put("/v1/workspaces/:workspaceId/integrations/slack/channel-routes", async (c) => {
    const workspaceId = c.req.param("workspaceId");
    // Same gate as the reaction-channel allowlist this sits beside: pointing a
    // channel somewhere is an installation-administration act.
    const grant = await requireAccessGrant(c, deps, workspaceId, "workspace:admin");
    const payload = UpdateSlackChannelRoutesRequest.parse(await c.req.json());
    const home = { accountId: grant.accountId, workspaceId };
    const installation = await listSlackInstallationBindings(deps.db, home);
    const binding = installation.find(
      (candidate) => candidate.connectionId === payload.connectionId,
    );
    // The connection must be this workspace's own installation, and the route
    // rows carry its team id, so a missing binding is a refusal rather than a
    // row written with an empty team the column's own bound would reject.
    if (!binding) {
      throw new HTTPException(404, { message: "Slack installation not found" });
    }
    // Authorize EVERY target before writing anything. Applying routes one at a
    // time means a refusal partway through would otherwise leave some channels
    // moved and the rest not, behind an error that says the save failed.
    //
    // An admin may only point a channel at a workspace they could start work in
    // themselves, and only inside this organization. The table's own CHECK
    // enforces the second half; this refuses before writing rather than
    // surfacing a constraint error.
    // A channel may never be pointed at a personal workspace. The routing
    // resolver already refuses to treat one as a candidate in a channel, and
    // this is the same rule at the write boundary, where a durable route would
    // otherwise smuggle it back in: every message in that channel would land
    // somewhere nobody else in the channel can see.
    //
    // Only the caller's OWN personal workspace has to be excluded, and that is
    // the whole rule rather than a partial one, because no other person's can
    // reach this loop. `resolveSlackTargetAuthority` admits exactly two shapes:
    // a `workspace_memberships` row this subject holds, or this subject's own
    // personal-workspace pointer. There is no account-admin union, so an
    // administrator does not thereby reach anyone else's personal workspace.
    //
    // Deliberately NOT justified by "the schema forbids a membership row on a
    // personal workspace". It does not. Migration 0219's `42501` is a
    // precondition inside the provisioning function, checked at provisioning
    // time only, and `personal_workspace_has_no_membership_row` is recorded as
    // `basis: "runtime"` - the parity report's own term for a property nothing
    // in the schema prevents. It holds because every membership writer either
    // requires `members:manage` on the target (which a personal-workspace grant
    // deliberately omits) or excludes personal workspaces outright.
    const callerPersonalWorkspaceId = await namedSubjectPersonalWorkspaceId(deps.db, {
      accountId: grant.accountId,
      subjectId: grant.subjectId,
    });
    for (const route of payload.routes) {
      if (route.targetWorkspaceId === null) continue;
      const authorized = await resolveSlackTargetAuthority(deps.db, {
        subjectId: grant.subjectId,
        targetAccountId: grant.accountId,
        targetWorkspaceId: route.targetWorkspaceId,
      });
      if (!authorized || !hasPermission(authorized.permissions, "sessions:create")) {
        throw new HTTPException(403, {
          message: `you cannot start work in the workspace chosen for ${route.slackChannelId}`,
        });
      }
      if (
        callerPersonalWorkspaceId !== null &&
        route.targetWorkspaceId === callerPersonalWorkspaceId
      ) {
        throw new HTTPException(422, {
          message: `a channel cannot be routed to a personal workspace: work started in ${route.slackChannelId} would be invisible to everyone else in it`,
        });
      }
    }
    for (const route of payload.routes) {
      if (route.targetWorkspaceId === null) {
        await deleteSlackChannelRoute(deps.db, home, {
          connectionId: payload.connectionId,
          slackChannelId: route.slackChannelId,
        });
        continue;
      }
      await upsertSlackChannelRoute(deps.db, home, {
        connectionId: payload.connectionId,
        slackTeamId: binding.slackTeamId,
        slackChannelId: route.slackChannelId,
        targetAccountId: grant.accountId,
        targetWorkspaceId: route.targetWorkspaceId,
        decidedBySubjectId: grant.subjectId,
        decidedBySlackUserId: "admin",
        source: "admin",
      });
    }
    return c.json({ ok: true });
  });

  app.get("/v1/workspaces/:workspaceId/integrations/slack/reaction-channels", async (c) => {
    const workspaceId = c.req.param("workspaceId");
    const grant = await requireAccessGrant(c, deps, workspaceId, "workspace:admin");
    const connectionId = boundedString(c.req.query("connectionId"), 64);
    if (!connectionId) throw new HTTPException(400, { message: "connectionId is required" });
    const cursor = boundedString(c.req.query("cursor"), 1_024);
    const client = await createOpenGeniSlackBotInteractionClient(deps, {
      accountId: grant.accountId,
      workspaceId,
      connectionId,
      subjectId: grant.subjectId,
    });
    const result = await client.listChannels({
      limit: 200,
      ...(cursor ? { cursor } : {}),
    });
    return c.json(
      SlackReactionChannelListResponse.parse({
        channels: result.channels
          .filter(
            (channel) =>
              channel.isMember &&
              !channel.isArchived &&
              !channel.isShared &&
              !channel.isExternallyShared &&
              !channel.isOrgShared,
          )
          .map((channel) => ({
            id: channel.id,
            name: channel.name,
            isPrivate: channel.isPrivate,
          })),
        nextCursor: result.nextCursor || null,
      }),
    );
  });
}

async function withCatalogSettings(
  deps: ApiRouteDeps,
  grant: Pick<AccessGrant, "accountId" | "workspaceId">,
): Promise<ApiRouteDeps> {
  const catalogSourceSettings = deps.catalogSourceSettings ?? deps.settings;
  return {
    ...deps,
    catalogSourceSettings,
    settings: (
      await resolveWorkspaceCatalogSettings(deps.db, catalogSourceSettings, {
        accountId: grant.accountId,
        workspaceId: grant.workspaceId,
      })
    ).settings,
  };
}

async function publishSlackAppHome(
  deps: ApiRouteDeps,
  installation: SlackInstallationRoute,
  refresh: SlackAppHomeRefresh,
  renewLease: () => Promise<void>,
): Promise<void> {
  const link = await getSlackBotUserLink(
    deps.db,
    installation.workspaceId,
    installation.connectionId,
    refresh.slackUserId,
  );
  const grant = link
    ? await getWorkspaceGrant(deps.db, link.subjectId, installation.workspaceId, {
        principalKind: "human_session",
      })
    : null;
  if (
    !grant ||
    grant.accountId !== installation.accountId ||
    !hasPermission(grant.permissions, "sessions:read")
  ) {
    const client = await createOpenGeniSlackBotInteractionClient(deps, {
      accountId: installation.accountId,
      workspaceId: installation.workspaceId,
      connectionId: installation.connectionId,
      subjectId: "service:slack-app-home",
    });
    await publishSlackAppHomeAccessView(
      client,
      refresh,
      renewLease,
      buildSlackAppHomeAccessBlocks({
        title: link ? "OpenGeni access needed" : "Connect your OpenGeni account",
        message: link
          ? "Your Slack identity is linked, but it does not currently have access to this OpenGeni workspace."
          : "Link this Slack identity to see your active tasks, requests, and recent results here.",
        actionLabel: link ? "Request access" : "Connect OpenGeni",
        actionUrl: slackAppHomeLinkUrl(
          deps,
          installation,
          refresh.slackTeamId,
          refresh.slackUserId,
        ),
      }),
    );
    return;
  }

  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    accountId: installation.accountId,
    workspaceId: installation.workspaceId,
    connectionId: installation.connectionId,
    subjectId: grant.subjectId,
  });
  // Host session authorization is resolved at publication time, before the
  // database query, so App Home cannot leak rows through a stale broad list.
  const authorizationScope = await requireSessionAuthorizationListScope(deps, grant, "core");
  const sessions: Awaited<ReturnType<typeof listSessionsForSubject>>["sessions"] = [];
  try {
    let cursor: ReturnType<typeof decodeSessionListCursor> | undefined;
    do {
      await renewLease();
      const page = await listSessionsForSubject(deps.db, installation.workspaceId, {
        subjectId: grant.subjectId,
        limit: 500,
        ...(cursor ? { cursor } : {}),
        ...(authorizationScope ? { authorizationScope } : {}),
      });
      if (!cursor) sessions.push(...page.pinned);
      sessions.push(...page.sessions);
      if (!page.nextCursor) break;
      cursor = decodeSessionListCursor(page.nextCursor) ?? undefined;
      if (!cursor) throw new Error("Slack App Home session cursor was invalid");
    } while (cursor);
  } catch (error) {
    if (!(error instanceof SessionListAccessError)) throw error;
    await publishSlackAppHomeAccessView(
      client,
      refresh,
      renewLease,
      buildSlackAppHomeAccessBlocks({
        title: "OpenGeni access changed",
        message:
          "Your current OpenGeni access could not be verified. Reconnect before tasks are shown here.",
        actionLabel: "Reconnect OpenGeni",
        actionUrl: slackAppHomeLinkUrl(
          deps,
          installation,
          refresh.slackTeamId,
          refresh.slackUserId,
        ),
      }),
    );
    return;
  }
  const currentLink = await getSlackBotUserLink(
    deps.db,
    installation.workspaceId,
    installation.connectionId,
    refresh.slackUserId,
  );
  const currentGrant = currentLink
    ? await getWorkspaceGrant(deps.db, currentLink.subjectId, installation.workspaceId, {
        principalKind: "human_session",
      })
    : null;
  if (
    !currentLink ||
    currentLink.subjectId !== grant.subjectId ||
    !currentGrant ||
    currentGrant.accountId !== installation.accountId ||
    !hasPermission(currentGrant.permissions, "sessions:read")
  ) {
    await publishSlackAppHomeAccessView(
      client,
      refresh,
      renewLease,
      buildSlackAppHomeAccessBlocks({
        title: "OpenGeni access changed",
        message:
          "Your current OpenGeni access could not be verified. Reconnect before tasks are shown here.",
        actionLabel: "Reconnect OpenGeni",
        actionUrl: slackAppHomeLinkUrl(
          deps,
          installation,
          refresh.slackTeamId,
          refresh.slackUserId,
        ),
      }),
    );
    return;
  }
  const currentAuthorizationScope = await requireSessionAuthorizationListScope(
    deps,
    currentGrant,
    "core",
  );
  if (
    slackSessionAuthorizationScopeKey(currentAuthorizationScope) !==
    slackSessionAuthorizationScopeKey(authorizationScope)
  ) {
    await publishSlackAppHomeAccessView(
      client,
      refresh,
      renewLease,
      buildSlackAppHomeAccessBlocks({
        title: "OpenGeni access changed",
        message:
          "Your current task access changed while this view was loading. Reopen Home to refresh it safely.",
        actionLabel: "Open OpenGeni",
        actionUrl: slackWorkspaceUrl(deps, installation.workspaceId),
      }),
    );
    return;
  }
  if (!refresh.providerViewHash) {
    await publishSlackAppHomeAccessView(
      client,
      refresh,
      renewLease,
      buildSlackAppHomeAccessBlocks({
        title: "Refresh OpenGeni Home",
        message:
          "Reopen Home to refresh your tasks safely. OpenGeni does not publish task data without Slack's current view version.",
        actionLabel: "Open OpenGeni",
        actionUrl: slackWorkspaceUrl(deps, installation.workspaceId),
      }),
    );
    return;
  }
  await renewLease();
  await client.publishHomeView({
    userId: refresh.slackUserId,
    hash: refresh.providerViewHash,
    blocks: buildSlackAppHomeBlocks({
      sessions,
      workspaceUrl: slackWorkspaceUrl(deps, installation.workspaceId),
      sessionUrl: (sessionId) => slackSessionUrl(deps, installation.workspaceId, sessionId),
    }),
  });
}

async function publishSlackAppHomeAccessView(
  client: OpenGeniSlackBotClient,
  refresh: SlackAppHomeRefresh,
  renewLease: () => Promise<void>,
  blocks: ReturnType<typeof buildSlackAppHomeAccessBlocks>,
): Promise<void> {
  try {
    await renewLease();
    await client.publishHomeView({
      userId: refresh.slackUserId,
      hash: refresh.providerViewHash,
      blocks,
    });
  } catch (error) {
    if (!(error instanceof SlackBotProviderError) || error.code !== "hash_conflict") throw error;
    // Access views contain no task data. If an older authorized publication
    // advanced Slack's optimistic-concurrency hash, replace it fail-closed
    // rather than dropping the newer clearing obligation.
    await renewLease();
    await client.publishHomeView({
      userId: refresh.slackUserId,
      blocks,
    });
  }
}

export async function drainSlackInteractionsOnce(deps: ApiRouteDeps): Promise<boolean> {
  const appHomeHolder = crypto.randomUUID();
  const refresh = await claimSlackAppHomeRefresh(deps.db, appHomeHolder, APP_HOME_LEASE_MS);
  if (refresh) {
    try {
      const installation = await resolveSlackInstallationRoute(deps.db, refresh.slackTeamId);
      if (
        !installation ||
        installation.accountId !== refresh.accountId ||
        installation.workspaceId !== refresh.workspaceId ||
        installation.connectionId !== refresh.connectionId
      ) {
        throw new SlackInteractionPermanentError("slack_app_home_installation_changed");
      }
      const renewLease = async () => {
        if (
          !(await renewSlackAppHomeRefreshClaim(deps.db, {
            refresh,
            claimHolderId: appHomeHolder,
            claimLeaseMs: APP_HOME_LEASE_MS,
          }))
        ) {
          throw new SlackInteractionPermanentError("slack_app_home_claim_lost");
        }
      };
      await renewLease();
      await publishSlackAppHome(deps, installation, refresh, renewLease);
      await settleSlackAppHomeRefresh(deps.db, {
        refresh,
        claimHolderId: appHomeHolder,
      });
    } catch (error) {
      const code = safeErrorCode(error);
      console.error("[slack-interactions] App Home refresh failed", {
        workspaceId: refresh.workspaceId,
        connectionId: refresh.connectionId,
        slackUserId: refresh.slackUserId,
        providerEventId: refresh.providerEventId,
        desiredRevision: refresh.desiredRevision,
        attemptCount: refresh.attemptCount,
        errorCode: code,
      });
      if (error instanceof SlackBotProviderError && error.code === "hash_conflict") {
        await settleSlackAppHomeRefresh(deps.db, {
          refresh,
          claimHolderId: appHomeHolder,
          errorCode: code,
        });
      } else if (
        refresh.attemptCount >= 5 ||
        error instanceof SlackInteractionPermanentError ||
        permanentSlackDeliveryError(error)
      ) {
        await settleSlackAppHomeRefresh(deps.db, {
          refresh,
          claimHolderId: appHomeHolder,
          errorCode: code,
        });
      } else {
        await releaseSlackAppHomeRefresh(deps.db, {
          refresh,
          claimHolderId: appHomeHolder,
          errorCode: code,
          retryAt: new Date(Date.now() + slackDeliveryRetryMs(error, refresh.attemptCount)),
        });
      }
    }
    return true;
  }

  const holder = crypto.randomUUID();
  const entry = await claimSlackInteractionInbox(deps.db, holder, INBOX_LEASE_MS);
  if (entry) {
    try {
      await processSlackInboxEntry(deps, entry);
      await settleSlackInteractionInbox(deps.db, {
        entry,
        claimHolderId: holder,
        outcome: "processed",
      });
    } catch (error) {
      const code = safeErrorCode(error);
      console.error("[slack-interactions] inbox processing failed", {
        workspaceId: entry.workspaceId,
        connectionId: entry.connectionId,
        providerEventId: entry.providerEventId,
        triggerKind: entry.triggerKind,
        attemptCount: entry.attemptCount,
        errorCode: code,
      });
      if (
        entry.attemptCount >= 5 ||
        permanentSlackInteractionError(error) ||
        permanentSlackDeliveryError(error)
      ) {
        await settleSlackInteractionInbox(deps.db, {
          entry,
          claimHolderId: holder,
          outcome: "failed",
          errorCode: code,
        });
      } else {
        await releaseSlackInteractionInbox(deps.db, {
          entry,
          claimHolderId: holder,
          errorCode: code,
          retryAt: new Date(Date.now() + slackDeliveryRetryMs(error, entry.attemptCount)),
        });
      }
    }
    return true;
  }

  const deliveryHolder = crypto.randomUUID();
  const interaction = await claimSlackInteractionDelivery(
    deps.db,
    deliveryHolder,
    DELIVERY_LEASE_MS,
  );
  if (!interaction) return false;
  try {
    await deliverSlackSessionEvents(deps, interaction, deliveryHolder);
  } catch (error) {
    const errorCode = slackDeliveryErrorCode(error);
    if (
      interaction.deliveryAttemptCount >= MAX_DELIVERY_ATTEMPTS ||
      permanentSlackDeliveryError(error)
    ) {
      await closeSlackInteractionDelivery(deps.db, {
        ...interaction,
        claimHolderId: deliveryHolder,
        sequence: interaction.lastDeliveredSessionEventSequence,
        state: "failed",
        errorCode,
      }).catch(() => undefined);
    } else {
      await deferSlackInteractionDelivery(deps.db, {
        ...interaction,
        claimHolderId: deliveryHolder,
        retryAt: new Date(
          Date.now() + slackDeliveryRetryMs(error, interaction.deliveryAttemptCount),
        ),
        errorCode,
      }).catch(() => undefined);
    }
  }
  return true;
}

export function startSlackInteractionPump(
  deps: ApiRouteDeps,
  options: { intervalMs?: number; maxPerTick?: number } = {},
): () => void {
  let stopped = false;
  let running = false;
  const intervalMs = Math.max(250, options.intervalMs ?? 1_000);
  const maxPerTick = Math.max(1, Math.min(50, options.maxPerTick ?? 10));
  const tick = async () => {
    if (stopped || running) return;
    running = true;
    try {
      for (let index = 0; index < maxPerTick; index += 1) {
        if (!(await drainSlackInteractionsOnce(deps))) break;
      }
    } finally {
      running = false;
    }
  };
  const timer = setInterval(() => void tick(), intervalMs);
  void tick();
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}

/**
 * A concurrent creator may have won this thread in another workspace between the
 * tenancy probe and the insert, in which case `getOrCreateSlackInteraction`
 * adopts that row. The grant, the imported attachments, and the resolved route
 * all belong to the workspace this pass chose, so none of them may be used
 * against the adopted row. Retry instead: the next pass sees the thread through
 * the probe, routes to it, and authorizes it properly.
 */
function requireSlackInteractionMatchesTarget(
  interaction: Pick<SlackInteraction, "accountId" | "workspaceId">,
  target: { accountId: string; workspaceId: string },
): void {
  if (
    interaction.accountId !== target.accountId ||
    interaction.workspaceId !== target.workspaceId
  ) {
    throw new SlackInteractionRetryableError("slack_route_creation_pending");
  }
}

/**
 * Gather the durable facts the routing decision needs, then decide.
 *
 * With the flag off nothing is read at all: the resolver short-circuits to the
 * installation's own workspace, so an existing single-workspace install issues
 * exactly the queries it issued before.
 */
async function resolveSlackRouteForEntry(
  deps: ApiRouteDeps,
  home: SlackRouteTenancy,
  entry: SlackInteractionInboxEntry,
  subjectId: string,
  options: {
    threadTenancy: SlackRouteTenancy | null;
    askEnabled: boolean;
    botUserId: string | null;
  },
): Promise<SlackRouteResolution> {
  const base = {
    home,
    entry,
    botUserId: options.botUserId,
    threadTenancy: options.threadTenancy,
    channelRoute: null,
    dmRoute: null,
    personalWorkspaceId: null,
    candidates: [] as const,
    routingEnabled: false,
    askEnabled: false,
  } as const;
  // A mapped thread wins outright, and with the flag off nothing is consulted at
  // all, so neither case reads anything.
  if (!deps.settings.slackWorkspaceRoutingEnabled || options.threadTenancy) {
    return resolveSlackWorkspaceRoute(base);
  }
  const directMessage = isSlackDirectMessageConversation(entry);
  const [channelRoute, dmRoute, candidates] = await Promise.all([
    directMessage
      ? Promise.resolve(null)
      : getSlackChannelRoute(deps.db, home, {
          connectionId: entry.connectionId,
          slackChannelId: entry.slackChannelId,
        }),
    directMessage
      ? getSlackUserDmRoute(deps.db, home, {
          connectionId: entry.connectionId,
          slackUserId: entry.slackUserId,
        })
      : Promise.resolve(null),
    listNamedSubjectSlackRoutableWorkspaces(deps.db, { accountId: home.accountId, subjectId }),
  ]);
  return resolveSlackWorkspaceRoute({
    ...base,
    channelRoute,
    dmRoute,
    candidates,
    // The candidate set is built from the same derived pointer, so reading it
    // again would be a second query for an answer already in hand.
    personalWorkspaceId: candidates.find((candidate) => candidate.personal)?.workspaceId ?? null,
    routingEnabled: true,
    askEnabled: options.askEnabled,
  });
}

/**
 * Tell the person exactly why nothing started, in their own bot DM.
 *
 * Every branch ends with "No session was created." because the one thing a
 * refusal must never do is leave someone believing work is under way.
 *
 * Only `no_access_to_route` mints an access-request link, and it mints one for
 * the workspace they actually need rather than the installation's. The other
 * two branches deliberately do not: a prefix naming something they cannot
 * reach, and an empty candidate list, both describe a workspace OpenGeni has
 * not established it will confirm to this person, so they point at an
 * administrator instead.
 */
async function postSlackRouteRefusal(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  entry: SlackInteractionInboxEntry,
  refusal: Extract<SlackRouteResolution, { kind: "denied" }> & Partial<SlackRouteTenancy>,
): Promise<void> {
  const available = refusal.candidates.map((candidate) => candidate.label);
  const text =
    refusal.reason === "no_access_to_named"
      ? `OpenGeni does not see a workspace named ${JSON.stringify(refusal.requested ?? "")} that you can start work in from here.${
          available.length > 0 ? ` You can use: ${available.join(", ")}.` : ""
        } No session was created.`
      : refusal.reason === "no_access_to_route"
        ? `OpenGeni starts work from this conversation in ${
            refusal.requested ?? "another workspace"
          }, and you do not have access to it. Request access: ${linkUrl(
            deps,
            {
              // Minted for the workspace they actually need, not the
              // installation's. The token only decides which workspace an
              // access request may be raised for; approval is unchanged.
              workspaceId: refusal.workspaceId ?? entry.workspaceId,
              connectionId: entry.connectionId,
              slackTeamId: entry.slackTeamId,
              slackUserId: entry.slackUserId,
            },
            entry.createdAt.getTime(),
          )}. No session was created.`
        : // Deliberately "from this conversation" rather than "for you": in a
          // channel this also fires for someone whose only workspace is their
          // own personal one, and telling that person they have no workspace
          // at all would be false. Their bot DM still works.
          "OpenGeni has no workspace it can start this task in from this conversation. Ask an OpenGeni administrator to give you access to one, or message OpenGeni directly to work in your own workspace. No session was created.";
  await client.postMessage({
    operationId: deterministicUuid(`slack-route-denied:${entry.id}:${refusal.reason}`),
    userId: entry.slackUserId,
    text: boundedOutput(text),
  });
}

/**
 * A workspace name as a Slack button label.
 *
 * Slack renders a button label as plain text and the stored option snapshot caps
 * it at 128 bytes, so bound it once at the boundary: the button, the row and
 * every outcome card then agree. Counted in UTF-8 bytes, because that is what
 * the CHECK counts.
 */
function boundedSlackRouteLabel(label: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(label).length <= 72) return label;
  let bounded = label;
  while (bounded.length > 0 && encoder.encode(`${bounded}...`).length > 72) {
    bounded = bounded.slice(0, -1);
  }
  return `${bounded}...`;
}

/**
 * Say where the work went, quietly.
 *
 * The line exists to stop work landing somewhere surprising, so it appears only
 * when the routing decision was a genuine choice: a null label means routing is
 * off, or the person had exactly one workspace anyway, and a constant footer on
 * every message would be noise rather than information.
 *
 * The label is frozen on the interaction rather than looked up here, because a
 * workspace rename between the original post and a reconciliation would make the
 * post ledger's byte comparison raise.
 */
export function withWorkspaceLine(
  label: string | null,
  text: string,
  blocks?: SlackMessageBlock[],
): { text: string; blocks?: SlackMessageBlock[] } {
  if (!label) return blocks ? { text, blocks } : { text };
  const suffix = `\n-> ${label}`;
  // Reserve the suffix rather than truncating after the fact, so the bounded
  // body plus the line still fits.
  const body = boundedOutput(text, MAX_SLACK_TEXT_CHARS - suffix.length);
  const line: SlackMessageBlock = {
    type: "context",
    elements: [{ type: "mrkdwn", text: `-> ${label}` }],
  };
  return {
    text: `${body}${suffix}`,
    ...(blocks ? { blocks: [...blocks, line] } : {}),
  };
}

/**
 * The post-ledger seed for one interaction's messages.
 *
 * The ledger binds one operation id to a digest over the rendered bytes, so
 * adding the workspace line under an unchanged id would wedge every interaction
 * with a claimed-but-unposted row. But bumping the id for interactions whose
 * bytes did NOT change is not free either: their ledger rows become unreachable,
 * so a repair or a cross-deploy retry posts a duplicate instead of finding the
 * completed original, and an action handle still points at the old operation.
 *
 * Only a labelled interaction renders different bytes, and the label is written
 * once at insert and never updated, so it is a stable discriminator. Version the
 * id on the label rather than on the release: a labelled interaction can only
 * have been created by an image that already renders the line, and every
 * pre-existing and unlabelled interaction keeps its exact ledger identity.
 */
export function slackPostSeed(
  interaction: Pick<SlackInteraction, "routedWorkspaceLabel">,
  seed: string,
): string {
  return interaction.routedWorkspaceLabel ? `${seed}:v2` : seed;
}

/** Sources that mean somebody, or something, actually chose this workspace. */
const SLACK_ROUTE_SOURCES_WORTH_NAMING: ReadonlySet<string> = new Set([
  // Not "thread": a mapped thread reuses the interaction that already exists,
  // so it inherits the label frozen when that interaction was created and never
  // reaches the writer below.
  "prefix",
  "channel",
  "dm_route",
  "dm_personal",
]);

/**
 * The label to freeze on a new interaction, or null to say nothing.
 *
 * `installation` and `sole_candidate` both mean there was only ever one place
 * this could go, so naming it on every message would be noise.
 *
 * Bounded with the picker's own button cap so the frozen label and the button
 * that produced it agree; the column allows 128 bytes, which is headroom.
 */
async function routedWorkspaceLabelFor(
  deps: ApiRouteDeps,
  resolution: Extract<SlackRouteResolution, { kind: "resolved" }>,
): Promise<string | null> {
  if (!SLACK_ROUTE_SOURCES_WORTH_NAMING.has(resolution.source)) return null;
  const label = resolution.label ?? (await getWorkspace(deps.db, resolution.workspaceId))?.name;
  if (!label) return null;
  const bounded = boundedSlackRouteLabel(label);
  return bounded.length > 0 ? bounded : null;
}

/** How long a first-use picker stays answerable. */
const SLACK_ROUTE_PROMPT_TTL_MS = 24 * 60 * 60 * 1000;

/** Buttons a card can carry before it stops being readable. */
const MAX_SLACK_ROUTE_PROMPT_BUTTONS = 5;

/**
 * Ask, once, where this conversation should work.
 *
 * The card is a bot DM rather than an ephemeral message: `chat.postEphemeral`
 * returns no durable `(channel, ts)` and no `client_msg_id` echo, and the whole
 * post ledger and its reconciliation are built on both.
 *
 * Nothing is created in any workspace here. The request is parked on the prompt
 * row and re-queued only once the person answers, so a picker that is never
 * answered leaves no session, no interaction and no half-started work.
 */
async function askSlackRouteChoice(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  home: SlackRouteTenancy,
  entry: SlackInteractionInboxEntry,
  candidates: readonly SlackRoutableWorkspace[],
): Promise<void> {
  const offered = candidates.slice(0, MAX_SLACK_ROUTE_PROMPT_BUTTONS);
  // Slack renders a button label as plain text and the stored snapshot caps it
  // at 128 bytes, so bound it once here: the button, the stored row and every
  // outcome card then agree, and the full name still shows in OpenGeni.
  const bounded = offered.map((candidate) => ({
    ...candidate,
    label: boundedSlackRouteLabel(candidate.label),
  }));
  const opened = await openSlackRoutePrompt(deps.db, home, {
    connectionId: entry.connectionId,
    inboxId: entry.id,
    slackTeamId: entry.slackTeamId,
    slackUserId: entry.slackUserId,
    slackChannelId: entry.slackChannelId,
    slackMessageTs: entry.slackMessageTs,
    providerEventId: entry.providerEventId,
    triggerKind: entry.triggerKind,
    requestText: entry.text,
    hasFiles: entry.hasFiles,
    slackThreadTs: entry.slackThreadTs,
    messageOperationId: deterministicUuid(`slack-route-prompt:${entry.id}`),
    expiresAt: new Date(Date.now() + SLACK_ROUTE_PROMPT_TTL_MS),
    candidates: bounded,
  });
  if (!opened) return;
  // The prompt commits before the card is posted, so a transient Slack failure
  // would otherwise leave a pending row with no card in front of anybody and
  // silently deaden the conversation. Post unconditionally instead: the
  // operation id is persisted on the prompt and the card is rendered from the
  // prompt's own stored options, so a replay reproduces identical bytes and the
  // post ledger returns the completed original rather than posting twice.
  const where = opened.prompt.slackChannelId.startsWith("D")
    ? "your messages with OpenGeni"
    : `<#${opened.prompt.slackChannelId}>`;
  const blocks: SlackMessageBlock[] = [
    {
      type: "section",
      text: {
        type: "mrkdwn",
        text: `OpenGeni can start this task in more than one workspace. Pick where ${where} should work. This choice is remembered.`,
      },
    },
    {
      type: "actions",
      block_id: "opengeni-route-choice",
      elements: [
        ...opened.prompt.options.map((option) => ({
          type: "button" as const,
          action_id: "opengeni.route.select",
          value: option.id,
          text: { type: "plain_text" as const, text: option.candidateLabel },
        })),
        {
          type: "button" as const,
          action_id: "opengeni.route.cancel",
          value: opened.prompt.options[0]?.id ?? opened.prompt.id,
          text: { type: "plain_text" as const, text: "Not now" },
        },
      ],
    },
  ];
  // Deliberately unconditional rather than derived from the live candidate
  // count: a replay must render identical bytes, and membership can change
  // between attempts.
  const url = slackWorkspaceUrl(deps, home.workspaceId);
  blocks.push({
    type: "section",
    text: {
      type: "mrkdwn",
      text: url
        ? `You can change this later in <${url}|OpenGeni>, under Capabilities, then Slack.`
        : "You can change this later in OpenGeni, under Capabilities, then Slack.",
    },
  });
  await client.postMessage({
    operationId: opened.prompt.messageOperationId,
    userId: opened.prompt.slackUserId,
    text: boundedOutput(
      `OpenGeni can start this task in more than one workspace. Pick where ${where} should work.`,
    ),
    blocks,
  });
}

/**
 * Process one click on a picker.
 *
 * The stored option row is a prompt-time snapshot and is NEVER authority: the
 * chosen workspace is re-authorized live, because access can have been revoked
 * between the question and the answer.
 */
async function handleSlackRouteChoice(
  deps: ApiRouteDeps,
  home: SlackRouteTenancy,
  entry: SlackInteractionInboxEntry,
  actionId: string,
  optionId: string,
): Promise<void> {
  if (!deps.settings.slackWorkspaceRoutingEnabled) {
    // A card can outlive the flag being turned back off. Honouring the click
    // would write a route row and start work through a path the operator has
    // disabled.
    throw new SlackInteractionPermanentError("slack_route_choice_unavailable");
  }
  const found = await getSlackRoutePromptByOption(deps.db, home, optionId);
  if (!found) throw new SlackInteractionPermanentError("slack_route_prompt_invalid");
  const { prompt, chosen } = found;
  // The same authorization pinning ordinary action handles use: a different
  // Slack human clicking someone else's card is ignored, not obeyed.
  if (prompt.slackUserId !== entry.slackUserId) {
    throw new SlackInteractionPermanentError("slack_route_prompt_user_mismatch");
  }
  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    ...home,
    connectionId: entry.connectionId,
    subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
  });
  // The post ledger binds one operation id to a digest over the text, so the
  // outcome has to be part of the seed. A second click on a settled card
  // renders different bytes than the first, and reusing one id would wedge the
  // ledger rather than saying so.
  const replaceCard = async (outcome: string, text: string) => {
    await client.updateMessage({
      operationId: deterministicUuid(
        `slack-route-answered:${prompt.id}:${actionId}:${chosen.id}:${outcome}`,
      ),
      channelId: entry.slackChannelId,
      timestamp: entry.slackMessageTs,
      text,
      // Every button goes with the card, so a sibling option cannot be pressed
      // after the question is settled.
      blocks: [{ type: "section", text: { type: "mrkdwn", text } }],
    });
  };

  if (actionId === "opengeni.route.cancel") {
    const cancelled = await settleSlackRoutePrompt(deps.db, home, {
      promptId: prompt.id,
      status: "cancelled",
    });
    await replaceCard(
      cancelled ? "cancelled" : "already_settled",
      cancelled
        ? "OpenGeni did not start this task. No session was created."
        : "OpenGeni already settled this choice.",
    );
    return;
  }

  const link = await getSlackBotUserLink(
    deps.db,
    home.workspaceId,
    entry.connectionId,
    entry.slackUserId,
  );
  const grant = link
    ? await resolveSlackTargetAuthority(deps.db, {
        subjectId: link.subjectId,
        targetAccountId: chosen.candidateAccountId,
        targetWorkspaceId: chosen.candidateWorkspaceId,
      })
    : null;
  if (!grant || !hasPermission(grant.permissions, "sessions:create")) {
    // Fail closed WITHOUT touching the card: the person may still have another
    // workspace on it that they can use, and replacing the card would take the
    // remaining buttons away and leave a pending prompt nobody can answer.
    await client.postMessage({
      operationId: deterministicUuid(`slack-route-denied:${prompt.id}:${chosen.id}`),
      userId: entry.slackUserId,
      text: boundedOutput(
        `OpenGeni cannot start work in ${chosen.candidateLabel}: you do not have access to it. No session was created. Pick another workspace on the card, or ask an OpenGeni administrator for access.`,
      ),
    });
    return;
  }

  const answered = await answerSlackRoutePrompt(deps.db, home, {
    promptId: prompt.id,
    targetAccountId: chosen.candidateAccountId,
    targetWorkspaceId: chosen.candidateWorkspaceId,
    answeredBySubjectId: grant.subjectId,
    decidedBySlackUserId: entry.slackUserId,
  });
  if (!answered.answered) {
    await replaceCard(
      "settled",
      "OpenGeni already settled this choice. No new session was created.",
    );
    return;
  }
  await replaceCard(
    "answered",
    `OpenGeni will use *${chosen.candidateLabel}* here. You can change this in OpenGeni under Capabilities, then Slack.`,
  );
}

async function processSlackInboxEntry(deps: ApiRouteDeps, entry: SlackInteractionInboxEntry) {
  if (entry.triggerKind === "block_action") {
    await processSlackBlockAction(deps, entry);
    return;
  }
  if (entry.triggerKind === "reaction") {
    await processSlackReactionInboxEntry(deps, entry);
    return;
  }
  // HOME is the installation binding's tenancy: it owns the bot credential and
  // connection, this inbox row, the identity links, and the post ledgers. TARGET
  // is the tenancy the resulting session lives in. They are equal today; the
  // routing resolver is what makes them diverge.
  const home = { accountId: entry.accountId, workspaceId: entry.workspaceId } as const;
  const installation = await resolveSlackInstallationRoute(deps.db, entry.slackTeamId);
  if (
    !installation ||
    installation.accountId !== entry.accountId ||
    installation.workspaceId !== entry.workspaceId ||
    installation.connectionId !== entry.connectionId
  ) {
    throw new SlackInteractionPermanentError("slack_task_installation_changed");
  }
  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    ...home,
    connectionId: entry.connectionId,
    subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
  });
  const policyResolution = isDirectMessageShortcut(entry)
    ? ({
        decision: {
          disposition: "ordinary",
          publication: "allow",
          reason: "ordinary_conversation",
        } as const,
        activePolicy: null,
      } as const)
    : await slackTaskPolicyDecision(deps, client, entry);
  const policyDecision = policyResolution.decision;
  if (policyDecision.disposition === "deny") {
    await client.postMessage({
      operationId: deterministicUuid(`slack-policy-denied:${entry.id}`),
      userId: entry.slackUserId,
      text: "OpenGeni cannot start a task from this shared conversation under the current workspace policy. No conversation content was read or retained.",
    });
    return;
  }
  const routePolicy = slackInteractionRoutePolicy(entry, {
    privateHandoff: policyDecision.disposition === "private_handoff",
  });
  const routeKey = routePolicy.initialRouteKey;
  // A mapped thread keeps the workspace it was created in, so the continuation
  // lookup is connection-scoped rather than workspace-fenced.
  const existing = await getSlackInteractionByConnectionRoute(deps.db, {
    accountId: home.accountId,
    connectionId: entry.connectionId,
    routeKey,
  });
  if (entry.triggerKind === "thread_reply" && !existing) return;
  // The identity link is a HOME fact: `slack_bot_user_links` is unique on
  // `(connection_id, slack_user_id)` and RLS-visible only under the
  // installation's own tenancy. It is read before the target is chosen because
  // the routing decision needs the subject it names.
  const link = await getSlackBotUserLink(
    deps.db,
    home.workspaceId,
    entry.connectionId,
    entry.slackUserId,
  );
  if (!link) {
    await client.postMessage({
      operationId: deterministicUuid(`slack-link:${entry.id}`),
      userId: entry.slackUserId,
      text: `Link your Slack identity to OpenGeni before starting work: ${linkUrl(deps, entry, entry.createdAt.getTime())}. No session was created.`,
    });
    return;
  }
  const resolution = await resolveSlackRouteForEntry(deps, home, entry, link.subjectId, {
    botUserId: installation.botUserId,
    threadTenancy: existing
      ? { accountId: existing.accountId, workspaceId: existing.workspaceId }
      : null,
    askEnabled: true,
  });
  if (resolution.kind === "ask") {
    await askSlackRouteChoice(deps, client, home, entry, resolution.candidates);
    return;
  }
  if (resolution.kind === "denied") {
    await postSlackRouteRefusal(deps, client, entry, resolution);
    return;
  }
  const target: { accountId: string; workspaceId: string } = {
    accountId: resolution.accountId,
    workspaceId: resolution.workspaceId,
  };
  const grant = await resolveSlackTargetAuthority(deps.db, {
    subjectId: link.subjectId,
    targetAccountId: target.accountId,
    targetWorkspaceId: target.workspaceId,
  });
  if (!grant) {
    // Never quietly serve this from a workspace the person did not name. A
    // routed workspace the subject cannot reach is a refusal with the existing
    // access-request path, minted for the workspace they actually need.
    if (resolution.source === "installation" || resolution.source === "thread") {
      throw new SlackInteractionPermanentError("identity_access_revoked");
    }
    await postSlackRouteRefusal(deps, client, entry, {
      kind: "denied",
      reason: "no_access_to_route",
      requested: resolution.label,
      candidates: [],
      ...target,
    });
    return;
  }
  // An override addresses the message; it is not part of the request.
  const routedEntry: SlackInteractionInboxEntry = {
    ...entry,
    text: slackRoutedRequestText(entry.text, resolution, installation.botUserId),
  };

  const alreadyDurable = await getSlackInteractionByClientEventId(
    deps.db,
    target.workspaceId,
    entry.connectionId,
    `slack:${entry.providerEventId}`,
  );
  if (alreadyDurable) {
    const { interaction, eventSessionId } = alreadyDurable;
    if (interaction.visibility === "private" && interaction.owningSubjectId !== grant.subjectId) {
      throw new SlackInteractionPermanentError("session_owner_mismatch");
    }
    if (interaction.sessionId !== null && interaction.sessionId !== eventSessionId) {
      throw new SlackInteractionPermanentError("slack_interaction_event_conflict");
    }
    const boundInteraction =
      interaction.sessionId !== null
        ? interaction
        : await bindSlackInteractionSession(deps.db, {
            ...interaction,
            owningSubjectId: grant.subjectId,
            sessionId: eventSessionId,
          });
    if (!boundInteraction) {
      throw new Error("Durable Slack interaction could not bind its reserved session");
    }
    await ensureSlackSharedTaskOrigin(deps, boundInteraction, entry, home, policyResolution);
    const shouldRepairAcknowledgement =
      interaction.triggeringProviderEventId === entry.providerEventId ||
      (usesPrivateBotDm(boundInteraction, entry) && boundInteraction.ackSlackMessageTs === null);
    if (shouldRepairAcknowledgement) {
      const boundClient = await createOpenGeniSlackBotInteractionClient(deps, {
        ...home,
        connectionId: entry.connectionId,
        subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
        sessionId: eventSessionId,
      });
      await acknowledgeSlackSession(deps, boundClient, boundInteraction, entry);
    }
    return;
  }

  let preparedEntry = routedEntry;
  let preparedModelContext: string | null = null;
  let preparedAttachments: PreparedSlackReactionTask = {
    resources: [],
    attachments: [],
    omissionCodes: [],
    omittedCount: 0,
  };
  if (entry.triggerKind === "app_mention" || entry.hasFiles) {
    const prepared = await prepareSlackInvocationEntry(
      deps,
      client,
      routedEntry,
      target,
      policyDecision.disposition === "private_handoff"
        ? async () => {
            await requireSlackSharedReadAuthorization(deps, client, entry, policyResolution);
          }
        : undefined,
    );
    preparedEntry = prepared.entry;
    preparedModelContext = prepared.modelContext;
    preparedAttachments = prepared.attachments;
  }

  if (existing?.sessionId) {
    const remounted = await remountSlackReactionTaskForSession(
      deps,
      target.workspaceId,
      existing.sessionId,
      preparedAttachments,
    );
    const prepared = slackInvocationPreparedMessage(preparedEntry, remounted, preparedModelContext);
    await continueSlackSession(deps, grant, existing, prepared.entry, remounted.resources, {
      modelContext: prepared.modelContext,
    });
    return;
  }
  if (!hasPermission(grant.permissions, "sessions:create")) {
    throw new SlackInteractionPermanentError("sessions_create_denied");
  }
  const { interaction } = await getOrCreateSlackInteraction(deps.db, {
    ...target,
    // Frozen here, after authorization, so a workspace name is never shown to
    // someone who could not reach it. Null when the routing decision was not a
    // genuine choice, which is what keeps the line off every message in an
    // install that only ever had one workspace.
    routedWorkspaceLabel: await routedWorkspaceLabelFor(deps, resolution),
    connectionId: entry.connectionId,
    slackTeamId: entry.slackTeamId,
    slackChannelId: entry.slackChannelId,
    slackThreadTs: entry.slackThreadTs ?? entry.slackMessageTs,
    routeKey,
    triggeringProviderEventId: entry.providerEventId,
    initiatingSlackUserId: entry.slackUserId,
    owningSubjectId: grant.subjectId,
    visibility: routePolicy.visibility,
  });
  requireSlackInteractionMatchesTarget(interaction, target);
  if (interaction.sessionId) {
    const remounted = await remountSlackReactionTaskForSession(
      deps,
      interaction.workspaceId,
      interaction.sessionId,
      preparedAttachments,
    );
    const prepared = slackInvocationPreparedMessage(preparedEntry, remounted, preparedModelContext);
    await continueSlackSession(deps, grant, interaction, prepared.entry, remounted.resources, {
      modelContext: prepared.modelContext,
    });
    return;
  }
  const preferredModel = await getLatestSessionModelForSubject(
    deps.db,
    interaction.workspaceId,
    grant.subjectId,
  );
  let session: Awaited<ReturnType<typeof createSessionForRequest>>;
  try {
    const prepared = slackInvocationPreparedMessage(
      preparedEntry,
      preparedAttachments,
      preparedModelContext,
    );
    session = await createSessionForRequest(
      await withCatalogSettings(deps, grant),
      grant,
      interaction.workspaceId,
      {
        requestedSessionId: interaction.sessionReservationId,
        initialMessage: prepared.entry.text,
        ...(prepared.modelContext ? { modelContext: prepared.modelContext } : {}),
        instructions: SLACK_SESSION_INSTRUCTIONS,
        firstPartyMcpTools: slackTaskFirstPartyMcpTools(deps.settings),
        resources: preparedAttachments.resources,
        ...(preferredModel ? { model: preferredModel } : {}),
        idempotencyKey: `slack:${entry.connectionId}:${entry.providerEventId}`,
        clientEventId: `slack:${entry.providerEventId}`,
      },
    );
  } catch (error) {
    if (error instanceof HTTPException) {
      await client.postMessage({
        operationId: deterministicUuid(`slack-admission-failed:${interaction.id}`),
        ...(routePolicy.directMessageShortcut
          ? { userId: entry.slackUserId }
          : {
              channelId: entry.slackChannelId,
              ...(entry.triggerKind === "slash_command"
                ? {}
                : { threadTimestamp: entry.slackThreadTs ?? entry.slackMessageTs }),
            }),
        text: slackAdmissionFailureText(error),
      });
    }
    throw error;
  }
  const bound = await bindSlackInteractionSession(deps.db, {
    ...interaction,
    owningSubjectId: grant.subjectId,
    sessionId: session.id,
  });
  if (!bound) throw new Error("Slack route could not bind its durable session");
  await ensureSlackSharedTaskOrigin(deps, bound, entry, home, policyResolution);
  const boundClient = await createOpenGeniSlackBotInteractionClient(deps, {
    ...home,
    connectionId: entry.connectionId,
    subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
    sessionId: session.id,
  });
  await acknowledgeSlackSession(deps, boundClient, bound, entry);
}

async function slackTaskPolicyDecision(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  entry: SlackInteractionInboxEntry,
) {
  const facts = await client.slackTaskPolicyFacts(entry.slackChannelId, entry.slackUserId);
  const activePolicy = facts.initiator
    ? await getActiveSlackTaskPolicy(deps.db, {
        accountId: entry.accountId,
        workspaceId: entry.workspaceId,
      })
    : null;
  const decision = evaluateSlackTaskPolicy({
    policy: activePolicy?.revision.policy ?? null,
    conversation: {
      installationTeamId: entry.slackTeamId,
      conversationId: facts.conversation.id,
      contextTeamId: facts.conversation.contextTeamId,
      connectedTeamIds: facts.conversation.connectedTeamIds,
      sharedTeamIds: facts.conversation.sharedTeamIds,
      isShared: facts.conversation.isShared,
      isExternallyShared: facts.conversation.isExternallyShared,
      isOrgShared: facts.conversation.isOrgShared,
      isPendingExternallyShared: facts.conversation.isPendingExternallyShared,
      isMpim: facts.conversation.isMpim,
    },
    initiator: facts.initiator ?? { teamId: null, isGuest: null, isExternal: null },
  });
  return { decision, activePolicy };
}

async function ensureSlackSharedTaskOrigin(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  entry: SlackInteractionInboxEntry,
  home: { accountId: string; workspaceId: string },
  resolution:
    | Awaited<ReturnType<typeof slackTaskPolicyDecision>>
    | {
        decision: {
          disposition: "ordinary";
          publication: "allow";
          reason: "ordinary_conversation";
        };
        activePolicy: null;
      },
): Promise<void> {
  if (resolution.decision.disposition !== "private_handoff") return;
  if (!resolution.activePolicy || !interaction.sessionId || !interaction.initiatingSlackUserId) {
    throw new SlackInteractionPermanentError("slack_shared_task_origin_incomplete");
  }
  await saveSlackSharedTaskOrigin(deps.db, {
    accountId: interaction.accountId,
    workspaceId: interaction.workspaceId,
    interactionId: interaction.id,
    connectionId: interaction.connectionId,
    sessionId: interaction.sessionId,
    slackTeamId: entry.slackTeamId,
    sourceChannelId: entry.slackChannelId,
    sourceThreadTs: entry.slackThreadTs ?? entry.slackMessageTs,
    initiatingSlackUserId: interaction.initiatingSlackUserId,
    // The task policy governs what may be read out of the Slack conversation,
    // which is an installation-surface concern, so its frozen revision keeps
    // home tenancy while the row itself follows the routed task.
    policyAccountId: home.accountId,
    policyWorkspaceId: home.workspaceId,
    policyRevisionId: resolution.activePolicy.revision.id,
    policyHash: resolution.activePolicy.revision.policyHash,
    policyActivationVersion: resolution.activePolicy.head.activationVersion,
    publicationMode: resolution.decision.publication,
  });
}

async function requireSlackSharedReadAuthorization(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  entry: SlackInteractionInboxEntry,
  resolution: Awaited<ReturnType<typeof slackTaskPolicyDecision>>,
): Promise<void> {
  const current = await slackTaskPolicyDecision(deps, client, entry);
  if (
    !resolution.activePolicy ||
    !current.activePolicy ||
    current.decision.disposition !== "private_handoff" ||
    current.activePolicy.revision.id !== resolution.activePolicy.revision.id ||
    current.activePolicy.revision.policyHash !== resolution.activePolicy.revision.policyHash ||
    current.activePolicy.head.activationVersion !== resolution.activePolicy.head.activationVersion
  ) {
    throw new SlackInteractionPermanentError("slack_shared_policy_changed_before_read");
  }
}

export type SlackInvocationMessageContext = {
  messages: Awaited<ReturnType<OpenGeniSlackBotClient["threadReplies"]>>["messages"];
  nextCursor: string | null;
  kind: "thread" | "channel";
};

async function prepareSlackInvocationEntry(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  entry: SlackInteractionInboxEntry,
  // Imported attachments become File resources of the session's workspace, so
  // they follow TARGET tenancy rather than the installation's.
  target: { accountId: string; workspaceId: string },
  authorizeRead?: () => Promise<void>,
): Promise<{
  entry: SlackInteractionInboxEntry;
  attachments: PreparedSlackReactionTask;
  modelContext: string | null;
}> {
  const context = entry.slackThreadTs
    ? await client.threadReplies({
        channelId: entry.slackChannelId,
        threadTimestamp: entry.slackThreadTs,
        limit: MAX_SLACK_INVOCATION_CONTEXT_MESSAGES,
        ...(authorizeRead ? { authorizeRead } : {}),
      })
    : await client.channelHistory({
        channelId: entry.slackChannelId,
        latest: entry.slackMessageTs,
        inclusive: true,
        limit: MAX_SLACK_CHANNEL_CONTEXT_MESSAGES,
        ...(authorizeRead ? { authorizeRead } : {}),
      });
  let exactMessage = context.messages.find((message) => message.timestamp === entry.slackMessageTs);
  if (!exactMessage && entry.hasFiles && entry.slackThreadTs) {
    const exact = await client.threadReplies({
      channelId: entry.slackChannelId,
      threadTimestamp: entry.slackThreadTs,
      oldest: entry.slackMessageTs,
      latest: entry.slackMessageTs,
      inclusive: true,
      limit: 1,
      ...(authorizeRead ? { authorizeRead } : {}),
    });
    exactMessage = exact.messages.find((message) => message.timestamp === entry.slackMessageTs);
  }
  const attachments = exactMessage
    ? await prepareSlackMessageAttachments(
        deps,
        client,
        entry,
        target,
        exactMessage.files,
        authorizeRead,
      )
    : {
        resources: [],
        attachments: [],
        omissionCodes: entry.hasFiles ? ["attachment_unavailable"] : [],
        omittedCount: entry.hasFiles ? 1 : 0,
      };
  const modelContext =
    entry.triggerKind === "app_mention"
      ? slackInvocationModelContext(entry.slackMessageTs, {
          messages: context.messages,
          nextCursor: context.nextCursor,
          kind: entry.slackThreadTs ? "thread" : "channel",
        })
      : null;
  return {
    entry,
    attachments,
    modelContext,
  };
}

function slackInvocationPreparedMessage(
  entry: SlackInteractionInboxEntry,
  attachments: PreparedSlackReactionTask,
  modelContext: string | null,
): { entry: SlackInteractionInboxEntry; modelContext: string | null } {
  const manifest = slackAttachmentManifest(
    attachments,
    "Imported invocation attachments",
    "invocation",
  );
  const combinedModelContext = [modelContext, manifest].filter(Boolean).join("\n\n");
  return {
    entry,
    modelContext: combinedModelContext.length > 0 ? combinedModelContext : null,
  };
}

export function slackInvocationModelContext(
  invocationTimestamp: string,
  context: SlackInvocationMessageContext,
) {
  const surroundingLines = context.messages
    .filter((message) => message.timestamp !== invocationTimestamp)
    .slice(0, MAX_SLACK_INVOCATION_CONTEXT_MESSAGES)
    .sort((left, right) => left.timestamp.localeCompare(right.timestamp))
    .map((message) => slackContextMessageLine(message));
  const contextLabel =
    context.kind === "thread"
      ? "Bounded containing-thread context (oldest to newest):"
      : "Bounded nearby channel context before the invocation (oldest to newest):";
  const truncationNotice =
    context.kind === "thread"
      ? "The containing thread was truncated at the bounded Slack context limit."
      : "Only bounded nearby channel context was provided.";
  let prompt = [
    "A linked, authorized Slack user explicitly mentioned OpenGeni.",
    "The visible user message on this turn is the exact accepted Slack invocation.",
    "Treat references such as 'this', 'that', or 'the previous message' as referring to the bounded Slack context below when applicable.",
    "Use this Slack content only as task-local input and do not persist it to Knowledge, Memory, preferences, policy, instructions, or the Workspace Charter unless separately authorized.",
    "",
    contextLabel,
  ].join("\n");
  let contextTruncated = context.nextCursor !== null;
  for (const line of surroundingLines) {
    const candidate = `${prompt}\n${line}`;
    if (candidate.length + 1 + truncationNotice.length > MAX_SLACK_INPUT_CHARS) {
      contextTruncated = true;
      break;
    }
    prompt = candidate;
  }
  return contextTruncated ? `${prompt}\n${truncationNotice}` : prompt;
}

async function acknowledgeSlackSession(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  interaction: SlackInteraction,
  entry: SlackInteractionInboxEntry,
) {
  if (!interaction.sessionId) {
    throw new Error("Slack acknowledgement requires a bound session");
  }
  const directMessageShortcut = isDirectMessageShortcut(entry);
  const privateHandoff =
    !directMessageShortcut && interaction.visibility === "private" && entry.triggerKind !== "dm";
  const privateBotDm = directMessageShortcut || privateHandoff;
  const operationId = deterministicUuid(slackPostSeed(interaction, `slack-ack:${interaction.id}`));
  // The acknowledgement carries exactly one session link plus the Status/Stop
  // buttons. The how-to prose it used to repeat forever now rides along once,
  // on this Slack identity's first accepted task in this installation.
  const started = directMessageShortcut
    ? `OpenGeni started a private task from the selected DM message. ${openSessionText(deps, interaction.workspaceId, interaction.sessionId)} The source DM was not opened to the bot or made workspace-visible.`
    : privateHandoff
      ? `OpenGeni started a private task from the selected Slack conversation. ${openSessionText(deps, interaction.workspaceId, interaction.sessionId)} Results stay private unless a separate authorized publication is approved.`
      : `OpenGeni started this task. ${openSessionText(deps, interaction.workspaceId, interaction.sessionId)}`;
  // The post ledger binds this fixed operation id to a digest over the message
  // text, and this function re-runs on every acknowledgement repair. Resolving
  // the hint freezes it durably before the post, so a repair renders identical
  // bytes; a failure here raises into the retryable inbox path rather than
  // binding the ledger to a hint-less acknowledgement it can never revise.
  const showHint = await resolveSlackInteractionFirstTaskHint(deps.db, {
    // The identity link is HOME; the interaction row is TARGET.
    workspaceId: entry.workspaceId,
    interactionWorkspaceId: interaction.workspaceId,
    connectionId: entry.connectionId,
    slackUserId: entry.slackUserId,
    interactionId: interaction.id,
  });
  const text = `${started}${showHint ? slackFirstTaskHintText(deps) : ""}`;
  const controls = await controlActionBlocks(deps, interaction, {
    messageOperationId: operationId,
    sessionEventSequence: 0,
    state: "active",
  });
  // The approval and control cards let `text` and `blocks[0]` diverge, so the
  // line is appended to each independently rather than assuming they match.
  const acknowledgement = withWorkspaceLine(
    interaction.routedWorkspaceLabel,
    text,
    controls.length > 0
      ? ([{ type: "section", text: { type: "mrkdwn", text } }, ...controls] as SlackMessageBlock[])
      : undefined,
  );
  const ack = await client.postMessage({
    operationId,
    ...(privateBotDm
      ? { userId: entry.slackUserId }
      : {
          channelId: entry.slackChannelId,
          ...(entry.triggerKind === "slash_command"
            ? {}
            : { threadTimestamp: entry.slackThreadTs ?? entry.slackMessageTs }),
        }),
    text: acknowledgement.text,
    ...(acknowledgement.blocks ? { blocks: acknowledgement.blocks } : {}),
  });
  if (entry.triggerKind === "slash_command" || privateBotDm) {
    const rekeyed = await rekeySlackInteractionRoute(deps.db, {
      ...interaction,
      routeKey: slackRouteKey(ack.channelId, ack.timestamp),
      slackChannelId: ack.channelId,
      slackThreadTs: ack.timestamp,
      ackSlackMessageTs: ack.timestamp,
      repairUnacknowledgedPrivateShortcutDelivery: privateBotDm,
    });
    if (!rekeyed) throw new Error("Slack acknowledgement could not rekey its durable route");
  }
}

/**
 * The one-time onboarding sentence shown on a Slack identity's first
 * acknowledged task in one installation.
 *
 * Rendering is a pure function of the configured command plus the frozen
 * `firstTaskHint` fact, so every re-render of the same acknowledgement produces
 * the same bytes for the digest-bound post ledger.
 *
 * Note the one remaining input that is not frozen: the text embeds
 * `settings.slackCommand`. Changing that setting between an acknowledgement
 * post and a later repair of the same interaction would diverge the digest and
 * conflict its post operation. The command is deployment configuration that
 * must already match the registered Slack slash command, so it does not change
 * under a live installation; a deployment that does rename it should drain
 * in-flight Slack interactions first.
 */
function slackFirstTaskHintText(deps: ApiRouteDeps): string {
  const command = deps.settings.slackCommand;
  return `\n\nFirst time here: reply in this thread to continue this task, or reply \`stop\` to stop it. Start a new top-level DM or run \`${command}\` again for a new task. Run \`${command} info\` any time for the full summary.`;
}

async function processSlackReactionInboxEntry(
  deps: ApiRouteDeps,
  entry: SlackInteractionInboxEntry,
) {
  // Reaction summon is configured on the installation surface, so the settings,
  // the connection scopes, and the identity link are all HOME reads.
  const home = { accountId: entry.accountId, workspaceId: entry.workspaceId } as const;
  const [workspace, connection, link] = await Promise.all([
    getWorkspace(deps.db, home.workspaceId),
    getConnectionMetadata(deps.db, home.workspaceId, entry.connectionId, null),
    getSlackBotUserLink(deps.db, home.workspaceId, entry.connectionId, entry.slackUserId),
  ]);
  const settings = resolveWorkspaceSlackReactionSummonSettings(workspace?.settings);
  if (
    !workspace ||
    !connection ||
    !settings.enabled ||
    entry.text !== settings.emoji ||
    !workspaceSlackReactionChannelAllowed(settings, entry.slackChannelId) ||
    !hasOpenGeniSlackReactionScope(connection.grantedScopes) ||
    !link
  ) {
    return;
  }
  // A reaction is never a direct message, so the routing decision here is the
  // channel's remembered answer, the sole candidate, or the installation's own
  // workspace. It runs before the provider context read so an unauthorized
  // subject never causes OpenGeni to read the Slack conversation.
  const routeResolution = await resolveSlackRouteForEntry(deps, home, entry, link.subjectId, {
    // A reaction carries no message text of the reacting person, so no prefix.
    botUserId: null,
    threadTenancy: null,
    askEnabled: false,
  });
  if (routeResolution.kind === "ask") {
    throw new SlackInteractionPermanentError("slack_route_choice_unavailable");
  }
  if (routeResolution.kind === "denied") {
    const summonClient = await createOpenGeniSlackBotInteractionClient(deps, {
      ...home,
      connectionId: entry.connectionId,
      subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
    });
    await postSlackRouteRefusal(deps, summonClient, entry, routeResolution);
    return;
  }
  let target: { accountId: string; workspaceId: string } = {
    accountId: routeResolution.accountId,
    workspaceId: routeResolution.workspaceId,
  };
  const authorizeTarget = async () => {
    const resolved = await resolveSlackTargetAuthority(deps.db, {
      subjectId: link.subjectId,
      targetAccountId: target.accountId,
      targetWorkspaceId: target.workspaceId,
    });
    if (!resolved) {
      throw new SlackInteractionPermanentError("identity_access_revoked");
    }
    if (
      !hasPermission(resolved.permissions, "sessions:create") ||
      !hasPermission(resolved.permissions, "sessions:control")
    ) {
      throw new SlackInteractionPermanentError("reaction_session_permissions_denied");
    }
    return resolved;
  };
  let grant = await authorizeTarget();

  const clientEventId = `slack:${entry.providerEventId}`;
  const durableInteraction = await getSlackInteractionByClientEventId(
    deps.db,
    target.workspaceId,
    entry.connectionId,
    clientEventId,
  );
  if (durableInteraction) {
    const { interaction, eventSessionId } = durableInteraction;
    const shouldRepairAcknowledgement =
      interaction.sessionId === null ||
      interaction.triggeringProviderEventId === entry.providerEventId;
    if (interaction.sessionId !== null && interaction.sessionId !== eventSessionId) {
      throw new SlackInteractionPermanentError("slack_reaction_event_conflict");
    }
    if (interaction.visibility === "private" && interaction.owningSubjectId !== grant.subjectId) {
      throw new SlackInteractionPermanentError("session_owner_mismatch");
    }
    if (interaction.sessionId === null && interaction.owningSubjectId !== grant.subjectId) {
      throw new SlackInteractionRetryableError("slack_route_creation_pending");
    }
    const boundInteraction =
      interaction.sessionId !== null
        ? interaction
        : await bindSlackInteractionSession(deps.db, {
            ...interaction,
            owningSubjectId: grant.subjectId,
            sessionId: eventSessionId,
          });
    if (!boundInteraction) {
      throw new Error("Durable Slack reaction route could not bind its reserved session");
    }
    await reopenSlackInteractionDelivery(deps.db, boundInteraction);
    if (shouldRepairAcknowledgement) {
      const client = await createOpenGeniSlackBotInteractionClient(deps, {
        ...home,
        connectionId: entry.connectionId,
        subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
        sessionId: eventSessionId,
      });
      await acknowledgeSlackReactionSession(deps, client, boundInteraction, settings.emoji);
    }
    return;
  }

  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    ...home,
    connectionId: entry.connectionId,
    subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
  });
  const context = await client.reactionMessageContext({
    channelId: entry.slackChannelId,
    messageTimestamp: entry.slackMessageTs,
    checkpoint: entry.reactionContextCheckpoint,
    checkpointBinding: {
      inboxId: entry.id,
      accountId: entry.accountId,
      workspaceId: entry.workspaceId,
      connectionId: entry.connectionId,
      providerEventId: entry.providerEventId,
      providerMessageId: entry.providerMessageId,
      slackTeamId: entry.slackTeamId,
      slackChannelId: entry.slackChannelId,
      slackMessageTs: entry.slackMessageTs,
    },
    saveCheckpoint: async (checkpoint) => {
      if (!entry.claimHolderId) {
        throw new Error("Slack reaction inbox checkpoint requires an active claim");
      }
      const saved = await saveSlackInteractionInboxReactionCheckpoint(deps.db, {
        entry,
        claimHolderId: entry.claimHolderId,
        checkpoint,
      });
      if (!saved) throw new Error("Slack reaction inbox checkpoint claim was lost");
    },
  });
  const routeKey = slackRouteKey(entry.slackChannelId, context.threadTimestamp);
  const existing = await getSlackInteractionByConnectionRoute(deps.db, {
    accountId: home.accountId,
    connectionId: entry.connectionId,
    routeKey,
  });
  if (
    existing &&
    (existing.accountId !== target.accountId || existing.workspaceId !== target.workspaceId)
  ) {
    // A mapped thread keeps the workspace it was created in.
    target = { accountId: existing.accountId, workspaceId: existing.workspaceId };
    grant = await authorizeTarget();
  }
  const preparedTask = await prepareSlackReactionTask(deps, client, entry, target, context);
  if (existing?.sessionId) {
    const appendedTask = await remountSlackReactionTaskForSession(
      deps,
      existing.workspaceId,
      existing.sessionId,
      preparedTask,
    );
    await continueSlackReactionSession(
      deps,
      grant,
      existing,
      slackReactionPreparedEntry(entry, context, appendedTask),
      appendedTask.resources,
    );
    return;
  }
  const { interaction } = await getOrCreateSlackInteraction(deps.db, {
    ...target,
    // A reaction routes exactly like a mention, so it names its workspace the
    // same way. Resolved after `authorizeTarget`, so a name never reaches
    // somebody who could not reach the workspace.
    routedWorkspaceLabel: await routedWorkspaceLabelFor(deps, routeResolution),
    connectionId: entry.connectionId,
    slackTeamId: entry.slackTeamId,
    slackChannelId: entry.slackChannelId,
    slackThreadTs: context.threadTimestamp,
    routeKey,
    triggeringProviderEventId: entry.providerEventId,
    initiatingSlackUserId: entry.slackUserId,
    owningSubjectId: grant.subjectId,
    visibility: "workspace",
  });
  requireSlackInteractionMatchesTarget(interaction, target);
  if (interaction.sessionId) {
    const appendedTask = await remountSlackReactionTaskForSession(
      deps,
      interaction.workspaceId,
      interaction.sessionId,
      preparedTask,
    );
    await continueSlackReactionSession(
      deps,
      grant,
      interaction,
      slackReactionPreparedEntry(entry, context, appendedTask),
      appendedTask.resources,
    );
    return;
  }
  if (interaction.owningSubjectId !== grant.subjectId) {
    // The route insert freezes the causal owner. Another linked user may race
    // the same previously-unmapped Slack thread on a different API replica,
    // but must not create the reserved session under their own authority.
    // Retry until the owner binds the session; the later user can then
    // continue the workspace-visible interaction through the normal path.
    throw new SlackInteractionRetryableError("slack_route_creation_pending");
  }
  const preferredModel = await getLatestSessionModelForSubject(
    deps.db,
    interaction.workspaceId,
    grant.subjectId,
  );
  const preparedEntry = slackReactionPreparedEntry(entry, context, preparedTask);
  let session: Awaited<ReturnType<typeof createSessionForRequest>>;
  try {
    session = await createSessionForRequest(
      await withCatalogSettings(deps, grant),
      grant,
      interaction.workspaceId,
      {
        requestedSessionId: interaction.sessionReservationId,
        initialMessage: preparedEntry.text,
        instructions: SLACK_SESSION_INSTRUCTIONS,
        // The exact reacted message and bounded containing thread are already in
        // the prompt; do not expose general Slack history tools for this trigger.
        firstPartyMcpTools: resolveFirstPartyMcpToolPolicy(deps.settings).default,
        resources: preparedTask.resources,
        ...(preferredModel ? { model: preferredModel } : {}),
        // Every reaction entry converging on this route must use the same create
        // key. This closes the same-owner multi-event race while the owner check
        // above prevents a different subject from winning creation authority.
        idempotencyKey: `slack-interaction:${interaction.id}`,
        clientEventId: `slack:${entry.providerEventId}`,
      },
    );
    // The route-wide create key converges every replica on one reserved
    // session, but its first writer's initial message is the only event created
    // by that operation. Replay this exact Slack event through the normal
    // per-message idempotency boundary: the create winner is recognized as the
    // initial event, while every distinct loser appends one durable task.
    await acceptSlackReactionTask(deps, grant, session.id, preparedEntry, preparedTask.resources);
  } catch (error) {
    if (error instanceof HTTPException) {
      await client.postMessage({
        operationId: deterministicUuid(`slack-reaction-admission-failed:${interaction.id}`),
        channelId: entry.slackChannelId,
        threadTimestamp: context.threadTimestamp,
        text: slackAdmissionFailureText(error),
      });
    }
    throw error;
  }
  const bound = await bindSlackInteractionSession(deps.db, {
    ...interaction,
    owningSubjectId: grant.subjectId,
    sessionId: session.id,
  });
  if (!bound) throw new Error("Slack reaction route could not bind its durable session");
  await acknowledgeSlackReactionSession(deps, client, bound, settings.emoji);
}

async function acknowledgeSlackReactionSession(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  interaction: SlackInteraction,
  emoji: string,
) {
  if (!interaction.sessionId) {
    throw new Error("Slack reaction acknowledgement requires a bound session");
  }
  const started = `OpenGeni started from the :${emoji}: reaction. ${openSessionText(deps, interaction.workspaceId, interaction.sessionId)} If the intended action is unclear, OpenGeni will ask in this thread. Reply here to continue, or reply \`stop\` to stop.`;
  const rendered = withWorkspaceLine(interaction.routedWorkspaceLabel, started);
  await client.postMessage({
    operationId: deterministicUuid(
      slackPostSeed(interaction, `slack-reaction-ack:${interaction.id}`),
    ),
    channelId: interaction.slackChannelId,
    threadTimestamp: interaction.slackThreadTs,
    text: rendered.text,
  });
}

type SlackReactionMessageContext = Awaited<
  ReturnType<OpenGeniSlackBotClient["reactionMessageContext"]>
>;

type PreparedSlackReactionTask = Readonly<{
  resources: FileResourceRef[];
  attachments: ImportedSlackReactionImage[];
  omissionCodes: string[];
  omittedCount: number;
}>;

function slackReactionPreparedEntry(
  entry: SlackInteractionInboxEntry,
  context: SlackReactionMessageContext,
  prepared: PreparedSlackReactionTask,
): SlackInteractionInboxEntry {
  return {
    ...entry,
    slackThreadTs: context.threadTimestamp,
    text: slackReactionTaskText(context, prepared),
  };
}

async function remountSlackReactionTaskForSession(
  deps: ApiRouteDeps,
  workspaceId: string,
  sessionId: string,
  prepared: PreparedSlackReactionTask,
): Promise<PreparedSlackReactionTask> {
  if (prepared.attachments.length === 0) return prepared;
  const session = await getSession(deps.db, workspaceId, sessionId);
  if (!session) throw new SlackInteractionPermanentError("session_not_found");
  const initialOrdinal = session.resources.reduce((maximum, resource) => {
    if (resource.kind !== "file" || !resource.mountPath) return maximum;
    const match = /^attachments\/slack\/(\d{2})-/u.exec(resource.mountPath);
    return match ? Math.max(maximum, Number(match[1])) : maximum;
  }, 0);
  const attachments = prepared.attachments.map((attachment, index) => {
    const basename = attachment.resource.mountPath?.replace(/^attachments\/slack\/\d{2}-/u, "");
    const ordinal = initialOrdinal + index + 1;
    if (ordinal > 99 || !basename) {
      throw new SlackInteractionPermanentError("slack_attachment_mount_exhausted");
    }
    const resource = Object.freeze({
      ...attachment.resource,
      mountPath: `attachments/slack/${String(ordinal).padStart(2, "0")}-${basename}`,
    });
    return Object.freeze({ ...attachment, resource });
  });
  return {
    ...prepared,
    attachments,
    resources: attachments.map((attachment) => attachment.resource),
  };
}

export function slackReactionTaskText(
  context: SlackReactionMessageContext,
  prepared: PreparedSlackReactionTask = {
    resources: [],
    attachments: [],
    omissionCodes: [],
    omittedCount: 0,
  },
) {
  const reactedLine = slackReactionMessageLine(context.reactedMessage, true);
  const surroundingLines = context.messages
    .slice(0, MAX_SLACK_REACTION_CONTEXT_MESSAGES)
    .filter((message) => message.timestamp !== context.reactedMessage.timestamp)
    .map((message) => slackReactionMessageLine(message, false));
  const truncationNotice =
    "The containing thread was truncated at the bounded Slack context limit.";
  let prompt = [
    "A linked, authorized Slack user explicitly summoned OpenGeni by reacting to one message.",
    "Use only the exact reacted message and bounded containing-thread context below.",
    "Execute a direct, safe, sufficiently specified request immediately.",
    "Ask one concise clarifying question only when materially required information is missing or the requested action is risky, irreversible, or authorization-sensitive.",
    "Do not infer permission to ingest or persist this Slack content into Knowledge, Memory, preferences, policy, instructions, or the Workspace Charter.",
    "",
    "Exact reacted message:",
    reactedLine,
    "",
    "Bounded surrounding thread context:",
  ].join("\n");
  let truncated = context.truncated;
  for (const line of surroundingLines) {
    const candidate = `${prompt}\n${line}`;
    if (candidate.length + 1 + truncationNotice.length > MAX_SLACK_INPUT_CHARS) {
      truncated = true;
      break;
    }
    prompt = candidate;
  }
  const attachmentManifest = slackReactionAttachmentManifest(prepared);
  if (attachmentManifest) {
    const candidate = `${prompt}\n\n${attachmentManifest}`;
    if (candidate.length + 1 + truncationNotice.length <= MAX_SLACK_INPUT_CHARS) {
      prompt = candidate;
    } else {
      truncated = true;
    }
  }
  return truncated ? `${prompt}\n${truncationNotice}` : prompt;
}

async function prepareSlackReactionTask(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  entry: SlackInteractionInboxEntry,
  target: { accountId: string; workspaceId: string },
  context: SlackReactionMessageContext,
): Promise<PreparedSlackReactionTask> {
  return await prepareSlackMessageAttachments(
    deps,
    client,
    entry,
    target,
    context.reactedMessage.files,
  );
}

async function prepareSlackMessageAttachments(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  entry: SlackInteractionInboxEntry,
  target: { accountId: string; workspaceId: string },
  exactFiles: readonly { id: string; name: string; title: string }[],
  authorizeSharedRead?: () => Promise<void>,
): Promise<PreparedSlackReactionTask> {
  if (exactFiles.length === 0) {
    return { resources: [], attachments: [], omissionCodes: [], omittedCount: 0 };
  }
  const selected = exactFiles.slice(0, MAX_SLACK_REACTION_IMAGES);
  const omissionCodes: string[] = [];
  let omittedCount = Math.max(0, exactFiles.length - selected.length);
  if (omittedCount > 0) omissionCodes.push("attachment_limit");

  // Complete every provider authorization/file-share preflight before any
  // download or workspace mutation. A revocation or exact-channel failure
  // therefore leaves zero imported objects even when it affects a later file.
  const prepared = await client.prepareReactionImageDownloads({
    channelId: entry.slackChannelId,
    files: selected.map((file) => ({
      id: file.id,
      name: file.name,
      title: file.title,
    })),
    ...(authorizeSharedRead ? { authorizeSharedRead } : {}),
  });
  const downloaded: Awaited<ReturnType<OpenGeniSlackBotClient["downloadReactionImage"]>>[] = [];
  let aggregateBytes = 0;
  for (const image of prepared) {
    try {
      const value = await client.downloadReactionImage(image, authorizeSharedRead);
      if (
        value.bytes.byteLength > SLACK_REACTION_IMAGE_MAX_BYTES ||
        aggregateBytes + value.bytes.byteLength > MAX_SLACK_REACTION_IMAGE_AGGREGATE_BYTES
      ) {
        omittedCount += 1;
        omissionCodes.push("attachment_size_limit");
        continue;
      }
      aggregateBytes += value.bytes.byteLength;
      downloaded.push(value);
    } catch (error) {
      if (slackReactionAuthorizationFailure(error)) throw error;
      omittedCount += 1;
      omissionCodes.push(slackReactionOmissionCode(error));
    }
  }

  if (!deps.objectStorage) {
    return {
      resources: [],
      attachments: [],
      omissionCodes: [...omissionCodes, "storage_unavailable"],
      omittedCount: omittedCount + downloaded.length,
    };
  }
  const attachments: ImportedSlackReactionImage[] = [];
  for (const image of downloaded) {
    try {
      attachments.push(
        await importSlackReactionImage(
          { db: deps.db, objectStorage: deps.objectStorage },
          {
            accountId: target.accountId,
            workspaceId: target.workspaceId,
            connectionId: entry.connectionId,
            slackTeamId: entry.slackTeamId,
            slackChannelId: entry.slackChannelId,
            slackMessageTs: entry.slackMessageTs,
          },
          image,
          attachments.length + 1,
        ),
      );
    } catch {
      omittedCount += 1;
      omissionCodes.push("storage_failed");
    }
  }
  return {
    resources: attachments.map((attachment) => attachment.resource),
    attachments,
    omissionCodes,
    omittedCount,
  };
}

function slackReactionAttachmentManifest(prepared: PreparedSlackReactionTask): string {
  return slackAttachmentManifest(
    prepared,
    "Imported reacted-message attachments",
    "reacted-message",
  );
}

function slackAttachmentManifest(
  prepared: PreparedSlackReactionTask,
  heading: string,
  sourceLabel: string,
): string {
  const lines = prepared.attachments.map(
    (attachment, index) =>
      `${index + 1}. ${attachment.resource.mountPath} (${attachment.contentType}, ${attachment.sizeBytes} bytes)`,
  );
  const omission =
    prepared.omittedCount > 0
      ? `Some ${sourceLabel} attachments were omitted (${prepared.omittedCount}; ${
          [...new Set(prepared.omissionCodes)].slice(0, 4).join(", ") || "unavailable"
        }). Continue with the available attachments and text.`
      : "";
  if (lines.length === 0) return omission;
  return [
    `${heading} (Slack order; aligned to attached resources):`,
    ...lines,
    ...(omission ? [omission] : []),
  ].join("\n");
}

function slackReactionAuthorizationFailure(error: unknown): boolean {
  if (!(error instanceof SlackBotProviderError)) return true;
  return new Set([
    "account_inactive",
    "channel_not_found",
    "file_not_found",
    "file_not_shared_to_channel",
    "http_401",
    "http_403",
    "invalid_auth",
    "missing_scope",
    "not_authed",
    "not_in_channel",
    "slack_connect_unsupported",
    "token_revoked",
  ]).has(error.code);
}

function slackReactionOmissionCode(error: unknown): string {
  if (!(error instanceof SlackBotProviderError)) return "attachment_unavailable";
  if (error.code === "unsupported_file_type" || error.code === "file_content_type_mismatch") {
    return "unsupported_image";
  }
  if (error.code.includes("size")) return "attachment_size_limit";
  return "attachment_unavailable";
}

function slackReactionMessageLine(
  message: SlackReactionMessageContext["reactedMessage"],
  reacted: boolean,
) {
  return slackContextMessageLine(message, reacted ? "reacted message" : undefined);
}

function slackContextMessageLine(
  message: SlackReactionMessageContext["reactedMessage"],
  annotation?: string,
) {
  const actor = message.userId || (message.botId ? `bot:${message.botId}` : "unknown");
  const text = message.text.trim() || "(no text)";
  const fileLabels: string[] = [];
  let fileChars = 0;
  let filesTruncated = false;
  for (const file of message.files) {
    const label = file.title || file.name || file.id;
    if (!label) continue;
    const addedChars = label.length + (fileLabels.length > 0 ? 2 : 0);
    if (fileChars + addedChars > MAX_SLACK_REACTION_FILE_SUMMARY_CHARS) {
      filesTruncated = true;
      break;
    }
    fileLabels.push(label);
    fileChars += addedChars;
  }
  const fileSummary = fileLabels.length
    ? ` Files: ${fileLabels.join(", ")}${filesTruncated ? ", …" : ""}.`
    : "";
  return `- ${message.timestamp || "unknown"} ${actor}${annotation ? ` [${annotation}]` : ""}: ${text}${fileSummary}`;
}

async function continueSlackReactionSession(
  deps: ApiRouteDeps,
  grant: AccessGrant,
  interaction: SlackInteraction,
  entry: SlackInteractionInboxEntry,
  resources: FileResourceRef[],
) {
  if (
    !interaction.sessionId ||
    (interaction.visibility === "private" && interaction.owningSubjectId !== grant.subjectId)
  ) {
    throw new SlackInteractionPermanentError("session_owner_mismatch");
  }
  await reopenSlackInteractionDelivery(deps.db, interaction);
  await acceptSlackReactionTask(deps, grant, interaction.sessionId, entry, resources);
}

async function acceptSlackReactionTask(
  deps: ApiRouteDeps,
  grant: AccessGrant,
  sessionId: string,
  entry: SlackInteractionInboxEntry,
  resources: FileResourceRef[],
) {
  const clientEventId = `slack:${entry.providerEventId}`;
  // Session events and user messages belong to the workspace that owns the
  // session, which is the workspace the grant authorized, not the installation.
  const existing = await getSessionEventByClientEventId(
    deps.db,
    grant.workspaceId,
    sessionId,
    clientEventId,
  );
  if (existing) {
    if (existing.type !== "user.message") {
      throw new SlackInteractionPermanentError("slack_reaction_event_conflict");
    }
    return;
  }
  await acceptSessionUserMessage(
    await withCatalogSettings(deps, grant),
    grant,
    grant.workspaceId,
    sessionId,
    {
      text: entry.text,
      resources,
      clientEventId,
    },
  );
}

async function continueSlackSession(
  deps: ApiRouteDeps,
  grant: AccessGrant,
  interaction: SlackInteraction,
  entry: SlackInteractionInboxEntry,
  resources: FileResourceRef[] = [],
  options: { modelContext?: string | null } = {},
) {
  if (
    !interaction.sessionId ||
    (interaction.visibility === "private" && interaction.owningSubjectId !== grant.subjectId)
  ) {
    throw new SlackInteractionPermanentError("session_owner_mismatch");
  }
  await reopenSlackInteractionDelivery(deps.db, interaction);
  if (entry.text.trim().toLowerCase() === "stop") {
    if (!hasPermission(grant.permissions, "sessions:control")) {
      throw new SlackInteractionPermanentError("sessions_control_denied");
    }
    await controlHumanSessionWorkstream(
      deps,
      {
        accountId: grant.accountId,
        workspaceId: grant.workspaceId,
        subjectId: grant.subjectId,
        sessionId: interaction.sessionId,
      },
      {
        action: "pause",
        clientEventId: deterministicUuid(`slack-stop:${entry.providerEventId}`),
        reason: "Stopped from the originating Slack thread",
      },
    );
    return;
  }
  const pending = await listSessionHumanInputRequests(
    deps.db,
    interaction.workspaceId,
    interaction.sessionId,
    { status: "pending", limit: 2 },
  );
  if (pending.length === 1) {
    const response = humanInputResponse(pending[0]!.questions, entry.text);
    if (response) {
      const accepted = await acceptSessionHumanInputResponse(deps.db, {
        accountId: grant.accountId,
        workspaceId: grant.workspaceId,
        sessionId: interaction.sessionId,
        requestId: pending[0]!.id,
        response,
        respondedBy: grant.subjectId,
        clientEventId: `slack:${entry.providerEventId}`,
      });
      if (accepted.action === "accepted" || accepted.action === "completed") {
        if (accepted.events.length > 0) {
          await publishDurableSessionEvents(
            deps.bus,
            grant.workspaceId,
            interaction.sessionId,
            accepted.events,
          );
        }
        if (accepted.workflowWakeRevision !== null) {
          await deps.workflowClient.signalApprovalDecision({
            accountId: grant.accountId,
            workspaceId: grant.workspaceId,
            sessionId: interaction.sessionId,
            eventId: accepted.events[0]?.id ?? pending[0]!.id,
            workflowId: `session-${interaction.sessionId}`,
            workflowWakeRevision: accepted.workflowWakeRevision,
          });
        }
        const committedOutcome = accepted.request.response?.outcome;
        if (committedOutcome === "answered" || committedOutcome === "skipped") return;
      }
    }
  }
  if (!hasPermission(grant.permissions, "sessions:control")) {
    throw new SlackInteractionPermanentError("sessions_control_denied");
  }
  await acceptSessionUserMessage(
    await withCatalogSettings(deps, grant),
    grant,
    interaction.workspaceId,
    interaction.sessionId,
    {
      text: entry.text,
      ...(options.modelContext ? { modelContext: options.modelContext } : {}),
      resources,
      clientEventId: `slack:${entry.providerEventId}`,
    },
  );
}

const SLACK_ACTION_ID_BY_KIND: Record<SlackInteractionActionKind, string> = {
  approval_approve: "opengeni.approval.approve",
  approval_reject: "opengeni.approval.reject",
  human_input_select: "opengeni.human_input.select",
  human_input_skip: "opengeni.human_input.skip",
  session_status: "opengeni.session.status",
  session_pause: "opengeni.session.pause",
  session_resume: "opengeni.session.resume",
  shared_result_publish: "opengeni.shared_result.publish",
};

async function processSlackBlockAction(deps: ApiRouteDeps, entry: SlackInteractionInboxEntry) {
  const separator = entry.text.lastIndexOf(":");
  const actionId = separator > 0 ? entry.text.slice(0, separator) : "";
  const handleId = separator > 0 ? entry.text.slice(separator + 1) : "";
  // The block-action inbox row carries HOME tenancy, exactly like every other
  // inbox row: it is the installation binding that received the click.
  const home = { accountId: entry.accountId, workspaceId: entry.workspaceId } as const;
  const route = await resolveSlackInstallationRoute(deps.db, entry.slackTeamId);
  if (
    !route ||
    route.accountId !== entry.accountId ||
    route.workspaceId !== entry.workspaceId ||
    route.connectionId !== entry.connectionId
  ) {
    throw new SlackInteractionPermanentError("slack_action_installation_changed");
  }
  // A route choice is not an action handle: the picker exists BEFORE any
  // session, so there is no handle row to load and the ordinary path below
  // would refuse it. Branch first.
  if (actionId === "opengeni.route.select" || actionId === "opengeni.route.cancel") {
    await handleSlackRouteChoice(deps, home, entry, actionId, handleId);
    return;
  }
  // A handle lives in the workspace that owns its session, which is not the
  // installation's workspace once routing is on. Learn that scope from the
  // content-free probe, then read the full handle under it: every authorization
  // check below is unchanged and still runs in the handle's own tenancy.
  const handleTenancy =
    (await probeSlackActionHandleTenancy(deps.db, {
      accountId: home.accountId,
      connectionId: entry.connectionId,
      handleId,
    })) ?? home;
  const handle = await getSlackInteractionActionHandle(deps.db, {
    ...handleTenancy,
    handleId,
  });
  if (!handle || SLACK_ACTION_ID_BY_KIND[handle.actionKind] !== actionId) {
    throw new SlackInteractionPermanentError("slack_action_handle_invalid");
  }
  // An action handle is a TARGET fact: it is composite-FK'd to its interaction
  // and its RESTRICTIVE session-visibility policy resolves the session under the
  // handle's own tenancy, so every read, settle and interaction lookup below
  // uses the handle's scope rather than the installation's.
  const target = { accountId: handle.accountId, workspaceId: handle.workspaceId } as const;
  if (handle.status !== "pending") return;
  if (handle.expiresAt.getTime() <= Date.now()) {
    await settleSlackInteractionActionHandles(deps.db, {
      ...target,
      handleId: handle.id,
      result: "expired",
      stale: true,
    });
    return;
  }
  if (handle.authorizedSlackUserId !== entry.slackUserId) {
    throw new SlackInteractionPermanentError("slack_action_user_mismatch");
  }
  const [interaction, link, post] = await Promise.all([
    getSlackInteractionById(deps.db, {
      ...target,
      interactionId: handle.interactionId,
    }),
    // Identity stays HOME.
    getSlackBotUserLink(deps.db, home.workspaceId, entry.connectionId, entry.slackUserId),
    // The post ledger is written by the bot credential, so it stays HOME too.
    getSlackBotPostOperation(
      deps.db,
      home.workspaceId,
      entry.connectionId,
      handle.messageOperationId,
    ),
  ]);
  if (
    !interaction ||
    interaction.connectionId !== entry.connectionId ||
    interaction.slackTeamId !== entry.slackTeamId ||
    interaction.sessionId !== handle.sessionId ||
    interaction.owningSubjectId !== handle.authorizedSubjectId ||
    interaction.initiatingSlackUserId !== handle.authorizedSlackUserId ||
    !link ||
    link.subjectId !== handle.authorizedSubjectId ||
    !post ||
    post.status !== "completed" ||
    post.slackChannelId !== entry.slackChannelId ||
    post.slackMessageTimestamp !== entry.slackMessageTs
  ) {
    throw new SlackInteractionPermanentError("slack_action_authority_changed");
  }
  const grant = await resolveSlackTargetAuthority(deps.db, {
    subjectId: link.subjectId,
    targetAccountId: target.accountId,
    targetWorkspaceId: target.workspaceId,
  });
  if (!grant || !hasPermission(grant.permissions, "sessions:control")) {
    throw new SlackInteractionPermanentError("sessions_control_denied");
  }
  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    ...home,
    connectionId: entry.connectionId,
    subjectId: grant.subjectId,
    sessionId: handle.sessionId,
  });
  const outcome = await executeSlackAction(deps, grant, interaction, handle);
  // A control click replaces the message it was pressed on. When that message
  // is this interaction's acknowledgement and this interaction won the one-time
  // onboarding hint, the update carries the hint forward: otherwise pressing
  // Status would permanently destroy the only copy of prose a Slack identity is
  // ever shown. The handle's message operation identifies the acknowledgement
  // exactly, independent of route rekeying. Later control cards have their own
  // operation ids, so the hint still appears on exactly one message.
  const acknowledgementOperationId = deterministicUuid(
    slackPostSeed(interaction, `slack-ack:${interaction.id}`),
  );
  const updateText =
    interaction.firstTaskHint === true && handle.messageOperationId === acknowledgementOperationId
      ? `${outcome.text}${slackFirstTaskHintText(deps)}`
      : outcome.text;
  // A control click replaces a message that already carried the line, so the
  // replacement carries it too rather than quietly stripping it.
  const rendered = withWorkspaceLine(interaction.routedWorkspaceLabel, updateText, [
    { type: "section", text: { type: "mrkdwn", text: updateText } },
  ]);
  await client.updateMessage({
    operationId: deterministicUuid(
      slackPostSeed(interaction, `slack-action-update:${handle.id}:${outcome.result}`),
    ),
    channelId: entry.slackChannelId,
    timestamp: entry.slackMessageTs,
    text: rendered.text,
    ...(rendered.blocks ? { blocks: rendered.blocks } : {}),
  });
  await settleSlackInteractionActionHandles(deps.db, {
    ...target,
    handleId: handle.id,
    result: outcome.result,
    ...(outcome.stale ? { stale: true } : {}),
  });
  if (outcome.controlState) {
    await postSlackControlCard(
      deps,
      client,
      interaction,
      outcome.controlState,
      entry.providerEventId,
    );
  }
}

async function executeSlackAction(
  deps: ApiRouteDeps,
  grant: AccessGrant,
  interaction: SlackInteraction,
  handle: SlackInteractionActionHandle,
): Promise<{
  result: string;
  text: string;
  stale?: boolean;
  controlState?: "active" | "paused";
}> {
  const mention = slackRequesterMention(interaction);
  if (handle.actionKind === "shared_result_publish") {
    return await publishSlackSharedResult(deps, grant, interaction, handle);
  }
  if (handle.actionKind === "approval_approve" || handle.actionKind === "approval_reject") {
    if (!handle.targetId) throw new SlackInteractionPermanentError("slack_action_target_invalid");
    const decision = handle.actionKind === "approval_approve" ? "approve" : "reject";
    const accepted = await acceptSessionApprovalDecision(deps.db, {
      accountId: grant.accountId,
      workspaceId: grant.workspaceId,
      sessionId: handle.sessionId,
      subjectId: grant.subjectId,
      payload: { approvalId: handle.targetId, decision },
      clientEventId: `slack-action:${handle.id}`,
    });
    if (accepted.action === "conflict") {
      return {
        result: "stale",
        stale: true,
        text: `${mention}This approval is no longer pending.`,
      };
    }
    await publishDurableSessionEvents(
      deps.bus,
      grant.workspaceId,
      handle.sessionId,
      accepted.events,
    );
    await deps.workflowClient.signalApprovalDecision({
      accountId: grant.accountId,
      workspaceId: grant.workspaceId,
      sessionId: handle.sessionId,
      eventId: accepted.event.id,
      workflowId: `session-${handle.sessionId}`,
      workflowWakeRevision: accepted.workflowWakeRevision,
    });
    return {
      result: decision === "approve" ? "approved" : "rejected",
      text: `${mention}${decision === "approve" ? "Approved once" : "Rejected"}. OpenGeni will continue from the durable task state.`,
    };
  }
  if (handle.actionKind === "human_input_select" || handle.actionKind === "human_input_skip") {
    if (!handle.targetId) throw new SlackInteractionPermanentError("slack_action_target_invalid");
    const request = await getSessionHumanInputRequest(
      deps.db,
      grant.workspaceId,
      handle.sessionId,
      handle.targetId,
    );
    if (!request || request.status !== "pending") {
      return {
        result: "stale",
        stale: true,
        text: `${mention}This question is no longer pending.`,
      };
    }
    let response:
      | { outcome: "skipped" }
      | {
          outcome: "answered";
          answers: Array<{ questionId: string; values: string[] }>;
        };
    if (handle.actionKind === "human_input_skip") {
      if (!request.allowSkip) {
        throw new SlackInteractionPermanentError("slack_action_skip_not_allowed");
      }
      response = { outcome: "skipped" };
    } else {
      let selected: unknown;
      try {
        selected = JSON.parse(handle.targetValue ?? "");
      } catch {
        throw new SlackInteractionPermanentError("slack_action_target_invalid");
      }
      if (
        !Array.isArray(selected) ||
        selected.length !== 2 ||
        typeof selected[0] !== "string" ||
        typeof selected[1] !== "string"
      ) {
        throw new SlackInteractionPermanentError("slack_action_target_invalid");
      }
      response = {
        outcome: "answered",
        answers: [{ questionId: selected[0], values: [selected[1]] }],
      };
    }
    const accepted = await acceptSessionHumanInputResponse(deps.db, {
      accountId: grant.accountId,
      workspaceId: grant.workspaceId,
      sessionId: handle.sessionId,
      requestId: handle.targetId,
      response,
      respondedBy: grant.subjectId,
      clientEventId: `slack-action:${handle.id}`,
    });
    if (accepted.action === "not_found" || accepted.action === "conflict") {
      return {
        result: "stale",
        stale: true,
        text: `${mention}This question is no longer pending.`,
      };
    }
    if (accepted.events.length > 0) {
      await publishDurableSessionEvents(
        deps.bus,
        grant.workspaceId,
        handle.sessionId,
        accepted.events,
      );
    }
    if (accepted.workflowWakeRevision !== null) {
      await deps.workflowClient.signalApprovalDecision({
        accountId: grant.accountId,
        workspaceId: grant.workspaceId,
        sessionId: handle.sessionId,
        eventId: accepted.events[0]?.id ?? handle.targetId,
        workflowId: `session-${handle.sessionId}`,
        workflowWakeRevision: accepted.workflowWakeRevision,
      });
    }
    const committedOutcome = accepted.request.response?.outcome ?? response.outcome;
    if (committedOutcome !== "answered" && committedOutcome !== "skipped") {
      return {
        result: "stale",
        stale: true,
        text: `${mention}This question is already ${committedOutcome}.`,
      };
    }
    return {
      result: committedOutcome === "skipped" ? "skipped" : "answered",
      text: `${mention}${committedOutcome === "skipped" ? "Skipped the question" : "Answer submitted"}. OpenGeni will continue.`,
    };
  }
  if (handle.actionKind === "session_pause" || handle.actionKind === "session_resume") {
    const action = handle.actionKind === "session_pause" ? "pause" : "resume";
    await controlHumanSessionWorkstream(
      deps,
      {
        accountId: grant.accountId,
        workspaceId: grant.workspaceId,
        subjectId: grant.subjectId,
        sessionId: handle.sessionId,
      },
      {
        action,
        clientEventId: deterministicUuid(`slack-action:${handle.id}`),
        reason: `${action === "pause" ? "Paused" : "Resumed"} from the originating Slack thread`,
      },
    );
    return {
      result: action === "pause" ? "paused" : "resumed",
      text: `${mention}OpenGeni task ${action === "pause" ? "paused" : "resumed"}.`,
      controlState: action === "pause" ? "paused" : "active",
    };
  }
  const session = await getSession(deps.db, grant.workspaceId, handle.sessionId);
  if (!session) throw new SlackInteractionPermanentError("slack_action_session_missing");
  // The result post no longer advertises recurrence. The Status card is where
  // the requester finds it, and only while this exact requester still holds
  // schedule authority in this workspace. The deep link and the schedules
  // authority behind it are unchanged: the browser rechecks both.
  const recurring =
    hasPermission(grant.permissions, "sessions:read") &&
    hasPermission(grant.permissions, "scheduled_tasks:manage")
      ? makeRecurringText(deps, grant.workspaceId, handle.sessionId)
      : null;
  return {
    result: "status",
    text: `${mention}OpenGeni task status: *${session.status.replaceAll("_", " ")}*.${
      recurring ? `\n\n${recurring}` : ""
    }`,
    ...(session.status === "cancelled" || session.status === "failed"
      ? {}
      : { controlState: "active" as const }),
  };
}

async function publishSlackSharedResult(
  deps: ApiRouteDeps,
  grant: AccessGrant,
  interaction: SlackInteraction,
  handle: SlackInteractionActionHandle,
): Promise<{ result: string; text: string; stale?: boolean }> {
  const mention = slackRequesterMention(interaction);
  if (!handle.targetId || !interaction.sessionId) {
    throw new SlackInteractionPermanentError("slack_shared_publication_target_invalid");
  }
  const publicationHome = await resolveSlackDeliveryHome(deps, interaction);
  const [origin, event] = await Promise.all([
    getSlackSharedTaskOrigin(deps.db, {
      accountId: interaction.accountId,
      workspaceId: interaction.workspaceId,
      interactionId: interaction.id,
    }),
    getSessionEvent(deps.db, interaction.workspaceId, handle.targetId),
  ]);
  // The Slack task policy governs what may be read out of, and published back
  // to, the Slack conversation, so it is an installation-surface fact. The
  // origin froze which tenancy that was; a row written before routing existed
  // carries null and implied its own, because the two could not differ then.
  const activePolicy = origin
    ? await getActiveSlackTaskPolicy(deps.db, {
        accountId: origin.policyAccountId ?? origin.accountId,
        workspaceId: origin.policyWorkspaceId ?? origin.workspaceId,
      })
    : null;
  if (
    !origin ||
    origin.connectionId !== interaction.connectionId ||
    origin.sessionId !== interaction.sessionId ||
    origin.initiatingSlackUserId !== handle.authorizedSlackUserId ||
    !activePolicy ||
    activePolicy.revision.id !== origin.policyRevisionId ||
    activePolicy.revision.policyHash !== origin.policyHash ||
    activePolicy.head.activationVersion !== origin.policyActivationVersion ||
    origin.publicationMode === "never" ||
    !event ||
    event.sessionId !== interaction.sessionId ||
    event.type !== "turn.completed"
  ) {
    return {
      result: "stale",
      stale: true,
      text: `${mention}This publication approval is stale. The result was not posted.`,
    };
  }
  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    ...publicationHome,
    connectionId: interaction.connectionId,
    subjectId: grant.subjectId,
    sessionId: interaction.sessionId,
  });
  let decision: ReturnType<typeof evaluateSlackTaskPolicy>;
  try {
    const facts = await client.slackTaskPolicyFacts(
      origin.sourceChannelId,
      origin.initiatingSlackUserId,
    );
    decision = evaluateSlackTaskPolicy({
      policy: activePolicy.revision.policy,
      conversation: {
        installationTeamId: origin.slackTeamId,
        conversationId: facts.conversation.id,
        contextTeamId: facts.conversation.contextTeamId,
        connectedTeamIds: facts.conversation.connectedTeamIds,
        sharedTeamIds: facts.conversation.sharedTeamIds,
        isShared: facts.conversation.isShared,
        isExternallyShared: facts.conversation.isExternallyShared,
        isOrgShared: facts.conversation.isOrgShared,
        isPendingExternallyShared: facts.conversation.isPendingExternallyShared,
        isMpim: facts.conversation.isMpim,
      },
      initiator: facts.initiator ?? { teamId: null, isGuest: null, isExternal: null },
    });
  } catch (error) {
    if (error instanceof SlackBotProviderError) {
      return {
        result: "stale",
        stale: true,
        text: `${mention}The shared conversation is no longer authorized. The result was not posted.`,
      };
    }
    throw error;
  }
  if (decision.disposition !== "private_handoff" || decision.publication === "never") {
    return {
      result: "stale",
      stale: true,
      text: `${mention}The shared conversation is no longer authorized. The result was not posted.`,
    };
  }
  const output = safePayloadText(event.payload, "output").trim();
  if (!output) {
    return {
      result: "stale",
      stale: true,
      text: `${mention}This result is no longer available for publication.`,
    };
  }
  await client.postMessage({
    operationId: deterministicUuid(
      `slack-shared-result:${origin.interactionId}:${event.id}:${origin.policyActivationVersion}`,
    ),
    channelId: origin.sourceChannelId,
    threadTimestamp: origin.sourceThreadTs,
    text: `<@${origin.initiatingSlackUserId}> ${boundedOutput(output)}`,
  });
  return {
    result: "published",
    text: `${mention}Published the approved result to the original shared conversation.`,
  };
}

function slackRequesterMention(interaction: Pick<SlackInteraction, "initiatingSlackUserId">) {
  return interaction.initiatingSlackUserId &&
    /^[UW][A-Z0-9]{1,63}$/.test(interaction.initiatingSlackUserId)
    ? `<@${interaction.initiatingSlackUserId}> `
    : "";
}

async function validatedSlackRequester(
  deps: ApiRouteDeps,
  interaction: Pick<
    SlackInteraction,
    "accountId" | "workspaceId" | "connectionId" | "initiatingSlackUserId" | "owningSubjectId"
  >,
  // Identity links are HOME facts, unique on (connection_id, slack_user_id) and
  // RLS-visible only under the installation's own tenancy.
  home: { accountId: string; workspaceId: string },
) {
  if (!interaction.initiatingSlackUserId) {
    return { authorized: false, mention: "" };
  }
  const link = await getSlackBotUserLink(
    deps.db,
    home.workspaceId,
    interaction.connectionId,
    interaction.initiatingSlackUserId,
  );
  const authorized = link?.subjectId === interaction.owningSubjectId;
  return {
    authorized,
    mention: authorized ? slackRequesterMention(interaction) : "",
  };
}

async function controlActionBlocks(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  input: {
    messageOperationId: string;
    sessionEventSequence: number;
    state: "active" | "paused";
  },
): Promise<SlackMessageBlock[]> {
  if (!interaction.sessionId || !interaction.initiatingSlackUserId) return [];
  const kinds: SlackInteractionActionKind[] = [
    "session_status",
    input.state === "paused" ? "session_resume" : "session_pause",
  ];
  const handles = await reserveSlackInteractionActionHandles(deps.db, {
    interaction,
    sessionEventSequence: input.sessionEventSequence,
    messageOperationId: input.messageOperationId,
    expiresAt: new Date(Date.now() + SLACK_ACTION_TTL_MS),
    actions: kinds.map((actionKind) => ({
      actionKind,
      actionKey: `${input.messageOperationId}:${actionKind}`,
    })),
  });
  const pending = handles.filter((handle) => handle.status === "pending");
  if (pending.length === 0) return [];
  return [
    {
      type: "actions",
      block_id: `opengeni_controls_${input.messageOperationId}`,
      elements: pending.map((handle) => ({
        type: "button" as const,
        action_id: SLACK_ACTION_ID_BY_KIND[handle.actionKind],
        value: handle.id,
        text: {
          type: "plain_text" as const,
          text:
            handle.actionKind === "session_status"
              ? "Status"
              : handle.actionKind === "session_pause"
                ? "Stop"
                : "Resume",
          emoji: true,
        },
        ...(handle.actionKind === "session_pause" ? { style: "danger" as const } : {}),
      })),
    },
  ];
}

async function postSlackControlCard(
  deps: ApiRouteDeps,
  client: OpenGeniSlackBotClient,
  interaction: SlackInteraction,
  state: "active" | "paused",
  providerEventId: string,
) {
  if (!interaction.sessionId) return;
  const operationId = deterministicUuid(
    `slack-control:${interaction.id}:${providerEventId}:${state}`,
  );
  const text = `${slackRequesterMention(interaction)}OpenGeni task controls.`;
  const controls = await controlActionBlocks(deps, interaction, {
    messageOperationId: operationId,
    sessionEventSequence: 0,
    state,
  });
  await client.postMessage({
    operationId,
    channelId: interaction.slackChannelId,
    threadTimestamp: interaction.slackThreadTs,
    text,
    blocks: [{ type: "section", text: { type: "mrkdwn", text } }, ...controls],
  });
}

type SlackApprovalSummary = { id: string; name: string };

function slackApprovalSummaries(payload: unknown): SlackApprovalSummary[] {
  const approvals = record(payload)?.approvals;
  if (!Array.isArray(approvals)) return [];
  return approvals
    .map((approval) => {
      const value = record(approval);
      const rawItem = record(value?.rawItem);
      const id = approvalIdentifier(approval);
      const name = boundedString(value?.name ?? value?.toolName ?? rawItem?.name, 256);
      return id ? { id, name: name ?? "OpenGeni action" } : null;
    })
    .filter((approval): approval is SlackApprovalSummary => approval !== null)
    .slice(0, MAX_SLACK_APPROVALS_PER_CARD);
}

async function slackApprovalCard(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  event: SessionEvent,
  mention: string,
  requesterAuthorized: boolean,
): Promise<{ text: string; blocks?: SlackMessageBlock[]; operationId: string }> {
  const operationId = deterministicUuid(
    slackPostSeed(interaction, `slack-delivery:${interaction.id}:${event.sequence}:approval`),
  );
  const approvals = slackApprovalSummaries(event.payload);
  const fallback = approvals.length
    ? `${mention}OpenGeni needs approval for ${approvals.map((approval) => approval.name).join(", ")}.`
    : `${mention}OpenGeni needs approval. Open the task to review it.`;
  if (
    !requesterAuthorized ||
    !interaction.sessionId ||
    !interaction.initiatingSlackUserId ||
    approvals.length === 0
  ) {
    return { text: fallback, operationId };
  }
  const specs = approvals
    .flatMap((approval) => [
      {
        actionKind: "approval_approve" as const,
        actionKey: `approval:${approval.id}:approve`,
        targetId: approval.id,
      },
      {
        actionKind: "approval_reject" as const,
        actionKey: `approval:${approval.id}:reject`,
        targetId: approval.id,
      },
    ])
    .slice(0, MAX_SLACK_ACTIONS_PER_CARD);
  const handles = await reserveSlackInteractionActionHandles(deps.db, {
    interaction,
    sessionEventSequence: event.sequence,
    messageOperationId: operationId,
    expiresAt: new Date(Date.now() + SLACK_ACTION_TTL_MS),
    actions: specs,
  });
  const byKey = new Map(handles.map((handle) => [handle.actionKey, handle]));
  const blocks: SlackMessageBlock[] = [];
  for (const approval of approvals) {
    const approve = byKey.get(`approval:${approval.id}:approve`);
    const reject = byKey.get(`approval:${approval.id}:reject`);
    blocks.push({
      type: "section",
      text: { type: "mrkdwn", text: `*Approval required*\n${approval.name}` },
    });
    if (approve?.status === "pending" && reject?.status === "pending") {
      blocks.push({
        type: "actions",
        block_id: `opengeni_approval_${event.sequence}_${blocks.length}`,
        elements: [
          {
            type: "button",
            action_id: SLACK_ACTION_ID_BY_KIND.approval_approve,
            value: approve.id,
            text: { type: "plain_text", text: "Approve once", emoji: true },
            style: "primary",
          },
          {
            type: "button",
            action_id: SLACK_ACTION_ID_BY_KIND.approval_reject,
            value: reject.id,
            text: { type: "plain_text", text: "Reject", emoji: true },
            style: "danger",
          },
        ],
      });
    }
  }
  return {
    text: fallback,
    blocks: [
      {
        type: "section",
        text: { type: "mrkdwn", text: `${mention}OpenGeni needs your approval.` },
      },
      ...blocks,
    ],
    operationId,
  };
}

async function slackSharedResultPublicationBlocks(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  event: SessionEvent,
  requesterAuthorized: boolean,
  messageOperationId: string,
): Promise<SlackMessageBlock[]> {
  if (!safePayloadText(event.payload, "output").trim()) return [];
  if (!requesterAuthorized || !interaction.sessionId || !interaction.initiatingSlackUserId) {
    return [];
  }
  const origin = await getSlackSharedTaskOrigin(deps.db, {
    accountId: interaction.accountId,
    workspaceId: interaction.workspaceId,
    interactionId: interaction.id,
  });
  if (!origin || origin.publicationMode === "never") return [];
  const [handle] = await reserveSlackInteractionActionHandles(deps.db, {
    interaction,
    sessionEventSequence: event.sequence,
    messageOperationId,
    expiresAt: new Date(Date.now() + SLACK_ACTION_TTL_MS),
    actions: [
      {
        actionKind: "shared_result_publish",
        actionKey: `shared-result:${event.id}:publish`,
        targetId: event.id,
      },
    ],
  });
  if (!handle || handle.status !== "pending") return [];
  return [
    {
      type: "actions",
      block_id: `opengeni_shared_result_${event.sequence}`,
      elements: [
        {
          type: "button",
          action_id: SLACK_ACTION_ID_BY_KIND.shared_result_publish,
          value: handle.id,
          text: {
            type: "plain_text",
            text:
              origin.publicationMode === "approval_required"
                ? "Approve & publish"
                : "Publish result",
            emoji: true,
          },
          style: "primary",
        },
      ],
    },
  ];
}

async function slackHumanInputCard(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  event: SessionEvent,
  mention: string,
  requesterAuthorized: boolean,
) {
  if (!interaction.sessionId) return null;
  const requestId = boundedString(record(record(event.payload)?.request)?.id, 64);
  if (!requestId) return null;
  const request = await getSessionHumanInputRequest(
    deps.db,
    interaction.workspaceId,
    interaction.sessionId,
    requestId,
  );
  if (!request || request.status !== "pending") return null;
  const operationId = deterministicUuid(
    slackPostSeed(interaction, `slack-delivery:${interaction.id}:${event.sequence}:human-input`),
  );
  const text = `${mention}OpenGeni needs your input:\n${formatQuestions(request.questions)}\nReply in this thread${request.allowSkip ? " or choose Skip" : ""}.`;
  if (!requesterAuthorized || !interaction.initiatingSlackUserId) return { text, operationId };
  const question = request.questions.length === 1 ? request.questions[0] : null;
  const canUseButtons =
    question?.kind === "single_select" &&
    question.options.length > 0 &&
    question.options.length <= 5;
  const actions: Array<{
    actionKind: SlackInteractionActionKind;
    actionKey: string;
    targetId: string;
    targetValue?: string;
  }> = [];
  if (canUseButtons && question) {
    for (const option of question.options) {
      actions.push({
        actionKind: "human_input_select",
        actionKey: `human:${request.id}:${question.id}:${option.id}`,
        targetId: request.id,
        targetValue: JSON.stringify([question.id, option.id]),
      });
    }
  }
  if (request.allowSkip) {
    actions.push({
      actionKind: "human_input_skip",
      actionKey: `human:${request.id}:skip`,
      targetId: request.id,
    });
  }
  if (actions.length === 0) return { text, operationId };
  const handles = await reserveSlackInteractionActionHandles(deps.db, {
    interaction,
    sessionEventSequence: event.sequence,
    messageOperationId: operationId,
    expiresAt: request.expiresAt
      ? new Date(Math.min(new Date(request.expiresAt).getTime(), Date.now() + SLACK_ACTION_TTL_MS))
      : new Date(Date.now() + SLACK_ACTION_TTL_MS),
    actions,
  });
  const pending = handles.filter((handle) => handle.status === "pending");
  const blocks: SlackMessageBlock[] = [{ type: "section", text: { type: "mrkdwn", text } }];
  if (pending.length > 0) {
    blocks.push({
      type: "actions",
      block_id: `opengeni_human_${event.sequence}`,
      elements: pending.map((handle) => {
        const option = question?.options.find(
          (candidate) => handle.targetValue === JSON.stringify([question.id, candidate.id]),
        );
        return {
          type: "button" as const,
          action_id: SLACK_ACTION_ID_BY_KIND[handle.actionKind],
          value: handle.id,
          text: {
            type: "plain_text" as const,
            text: handle.actionKind === "human_input_skip" ? "Skip" : (option?.label ?? "Choose"),
            emoji: true,
          },
        };
      }),
    });
  }
  return { text, blocks, operationId };
}

/**
 * A child of a Slack-originated session is never itself mapped to a Slack
 * thread, so the parent's bounded `child_requires_action` notice is the only
 * path that can tell the human that a worker they started is blocked. The card
 * is a pointer, not a second question card: one bounded preview of the first
 * question (or the waiting approval count) plus the child link. The child's own
 * OpenGeni card remains the place the human actually answers.
 *
 * Every deferred child lifecycle kind (`child_progress`,
 * `child_waiting_capacity`, `child_requires_action_resolved`, `child_paused`)
 * returns null here so Slack stays quiet, and so does every non-child machine
 * input that shares the `system.update.pending` event type.
 *
 * The caller has already proven that this workspace turned the notice on. The
 * card defaults off per workspace because the in-app rail and priority feed
 * already surface a blocked child.
 */
/**
 * The exact durable notice behind one `system.update.pending` event, or null
 * when it no longer describes a blocked worker.
 *
 * Slack delivery runs behind the session: a widened retry window, a replica
 * claim, or simply a later page can reach this event after the child already
 * got its answer. Two facts have to agree before a card is worth posting.
 *
 * A resolution supersedes a still-`pending` notice in the same commit, so
 * `superseded` (and an explicitly `cancelled` notice) is already a "no longer
 * blocked" fact. But a notice the parent's turn has claimed is `delivered` and
 * keeps that state forever, so state alone is not enough - the resolution row
 * for that exact (child, turn, generation) boundary is checked as well, on the
 * parent's own rows. A re-freeze is a new generation and a new notice.
 *
 * Every read is wrapped: a malformed or unreadable durable row is not a
 * delivery failure. Throwing would burn a delivery attempt and, after
 * MAX_DELIVERY_ATTEMPTS, close this interaction's whole delivery over one
 * notice nobody could have posted anyway. Silence is the correct outcome, and
 * it is the same outcome every other unpostable notice already takes.
 */
async function resolveSlackBlockedChildNotice(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  sessionId: string,
  updateId: string,
): Promise<ChildRequiresActionPayload | null> {
  try {
    const update = await getSessionSystemUpdateById(
      deps.db,
      interaction.workspaceId,
      sessionId,
      updateId,
    );
    if (!update || update.payload.type !== "child_requires_action") return null;
    if (update.state === "superseded" || update.state === "cancelled") return null;
    const resolved = await childRequiresActionResolutionExists(
      deps.db,
      interaction.workspaceId,
      sessionId,
      {
        childSessionId: update.payload.childSessionId,
        childTurnId: update.payload.childTurnId,
        childTurnGeneration: update.payload.childTurnGeneration,
      },
    );
    return resolved ? null : update.payload;
  } catch {
    return null;
  }
}

async function slackChildRequiresActionCard(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  event: SessionEvent,
  mention: string,
): Promise<{ text: string } | null> {
  const sessionId = interaction.sessionId;
  if (!sessionId) return null;
  const preview = record(event.payload);
  if (boundedString(preview?.kind, 64) !== "child_requires_action") return null;
  const updateId = boundedString(preview?.updateId, 64);
  if (!updateId || !SLACK_UUID_PATTERN.test(updateId)) return null;
  // The bounded event preview is lossy by construction. Resolve the exact
  // durable notice under the ordinary workspace scope; the child session is
  // never read, only linked.
  const notice = await resolveSlackBlockedChildNotice(deps, interaction, sessionId, updateId);
  if (!notice) return null;
  const questions = notice.requests.filter((request) => request.kind === "human_input");
  const approvals = notice.requests.filter((request) => request.kind === "approval");
  const first = questions[0];
  const detail = first
    ? `${boundedSlackChildDetail(first.firstQuestion)}${
        first.questionCount > 1 ? ` (+${first.questionCount - 1} more)` : ""
      }`
    : approvals.length > 0
      ? `${approvals.length} tool approval${approvals.length > 1 ? "s are" : " is"} waiting for a human.`
      : "";
  const link = slackSessionUrl(deps, interaction.workspaceId, notice.childSessionId);
  const lines = [`${mention}A worker you started needs input.`];
  if (detail) lines.push(`> ${detail}`);
  if (link) lines.push(`<${link}|Open in OpenGeni>`);
  return { text: boundedOutput(lines.join("\n")) };
}

/**
 * A goal that paused because it ran out of budget or hit the continuation cap
 * stops making progress with nobody watching, so the Slack thread gets one
 * bounded line. A `user_pause` / `api` / `agent` pause is a decision the human
 * or their agent already made, and `no_progress` is not a stop the human must
 * act on, so none of them post. `goal.resumed` never posts.
 *
 * The caller has already proven that this workspace turned the notice on; it
 * defaults off per workspace.
 */
function slackGoalPausedText(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  event: SessionEvent,
  mention: string,
): string | null {
  if (!interaction.sessionId) return null;
  const reason = boundedString(record(event.payload)?.reason, 64);
  const headline = reason ? SLACK_GOAL_PAUSED_HEADLINES.get(reason) : undefined;
  if (!headline) return null;
  const link = slackSessionUrl(deps, interaction.workspaceId, interaction.sessionId);
  return boundedOutput(`${mention}${headline}.${link ? ` <${link}|Open in OpenGeni>` : ""}`);
}

/**
 * The installation tenancy that owns this interaction's Slack credential.
 *
 * `slack_interactions.connection_id` deliberately has no composite
 * `(workspace_id, connection_id)` foreign key, so a routed interaction may point
 * at the installation's connection from another workspace.
 *
 * A binding that does not resolve, or that now names a different connection, is
 * NOT turned into a new delivery precondition: this code previously used the
 * interaction's own tenancy unconditionally, so falling back to it keeps every
 * delivery that used to succeed succeeding. The fallback cannot post from the
 * wrong workspace either, because `claimSlackBotPostOperation` selects the
 * connection under the tenancy it is given and fails when it does not own it.
 */
async function resolveSlackDeliveryHome(
  deps: ApiRouteDeps,
  interaction: Pick<SlackInteraction, "accountId" | "workspaceId" | "connectionId" | "slackTeamId">,
): Promise<{ accountId: string; workspaceId: string }> {
  const installation = await resolveSlackInstallationRoute(deps.db, interaction.slackTeamId);
  return installation && installation.connectionId === interaction.connectionId
    ? { accountId: installation.accountId, workspaceId: installation.workspaceId }
    : { accountId: interaction.accountId, workspaceId: interaction.workspaceId };
}

async function deliverSlackSessionEvents(
  deps: ApiRouteDeps,
  interaction: SlackInteraction,
  claimHolderId: string,
) {
  if (!interaction.sessionId) return;
  const page = await listSessionEventPage(deps.db, interaction.workspaceId, interaction.sessionId, {
    after: interaction.lastDeliveredSessionEventSequence,
    limit: 100,
    includeTypes: [...SLACK_DELIVERY_EVENT_TYPES],
    maxBytes: 256 * 1024,
  });
  if (page.events.length === 0) {
    await releaseSlackInteractionDelivery(deps.db, {
      ...interaction,
      claimHolderId,
    });
    return;
  }
  // The bot credential is owned by the installation's workspace and every
  // provider call is fenced on it, so the delivery client is always built from
  // HOME even when the session it reports on lives in another workspace.
  const home = await resolveSlackDeliveryHome(deps, interaction);
  const client = await createOpenGeniSlackBotInteractionClient(deps, {
    ...home,
    connectionId: interaction.connectionId,
    subjectId: SLACK_INTERACTION_BOT_SUBJECT_ID,
    sessionId: interaction.sessionId,
  });
  const requester = await validatedSlackRequester(deps, interaction, home);
  // Both orchestration notices are per-workspace and OFF unless this workspace
  // turned them on. Resolved lazily so an ordinary page of turn/progress events
  // costs no extra workspace read, and memoized so one page resolves once. A
  // disabled notice takes the same "nothing to post for this event" path as an
  // undeliverable one: no post operation, no progress slot, and the delivery
  // cursor still advances past the event exactly as it would have.
  let orchestrationNotices: ResolvedWorkspaceSlackOrchestrationNoticeSettings | null = null;
  const orchestrationNoticeEnabled = async (
    notice: keyof ResolvedWorkspaceSlackOrchestrationNoticeSettings,
  ): Promise<boolean> => {
    orchestrationNotices ??= resolveWorkspaceSlackOrchestrationNoticeSettings(
      (await getWorkspace(deps.db, interaction.workspaceId))?.settings,
    );
    return orchestrationNotices[notice];
  };
  /**
   * Post one orchestration notice through the SAME durable per-interaction slot
   * budget as assistant progress, so an orchestration that fans out to many
   * blocked children cannot turn one Slack task into an unbounded feed. That
   * budget is deliberately shared rather than a second private allowance: the
   * ceiling worth enforcing is the total number of posts the human did not ask
   * for, not a per-category one, and this whole feature exists because an
   * unsolicited post is worse than a missed one. Beyond the cap the notice goes
   * silent exactly like a fourth progress message.
   *
   * The slot is claimed only once a card is actually going to be posted, so a
   * skipped, stale, or already-resolved notice never burns one. Claims are
   * durable and keyed on the session event sequence, so a reaper retry, a
   * replica claim, or a replayed page reuses the same slot and the same post
   * operation instead of posting twice or consuming a second slot. The claimed
   * row never becomes terminal-coalescing evidence: that lookup only joins
   * `agent.message.completed` events.
   */
  const postUnsolicitedNotice = async (event: SessionEvent, text: string, kind: string) => {
    const slot = await claimSlackInteractionProgressDelivery(deps.db, {
      accountId: interaction.accountId,
      workspaceId: interaction.workspaceId,
      interactionId: interaction.id,
      claimHolderId,
      sessionEventSequence: event.sequence,
      maxProgress: MAX_PROGRESS_MESSAGES,
    });
    if (slot.kind === "not_owned") {
      throw new Error("Slack orchestration notice lost its durable interaction claim");
    }
    if (slot.kind !== "claimed") return;
    await postDelivery(client, interaction, event, text, kind, slot.delivery.operationId);
  };
  let lastSequence = interaction.lastDeliveredSessionEventSequence;
  let terminal: Exclude<SlackInteraction["terminalDeliveryState"], "open"> | null = null;
  const orderedEvents = page.events
    .filter(
      (event) =>
        (event.turnAssociation === null || event.turnAssociation === "current") &&
        event.duplicateOfEventId === null,
    )
    .sort((left, right) => left.sequence - right.sequence);
  lastSequence = page.events.reduce(
    (latest, event) => Math.max(latest, event.sequence),
    lastSequence,
  );
  const terminalAssistantSequences = new Set<number>();
  for (let index = 0; index < orderedEvents.length; index += 1) {
    const event = orderedEvents[index]!;
    if (event.type !== "turn.completed") continue;
    const finalOutput = safePayloadText(event.payload, "output").trim();
    if (!finalOutput || safePayloadText(event.payload, "segmentLimit")) continue;
    const candidates: SessionEvent[] = [];
    for (let candidateIndex = index - 1; candidateIndex >= 0; candidateIndex -= 1) {
      const candidate = orderedEvents[candidateIndex]!;
      if (
        candidate.type === "turn.completed" ||
        candidate.type === "turn.failed" ||
        candidate.type === "turn.cancelled"
      ) {
        break;
      }
      if (candidate.type !== "agent.message.completed") continue;
      if (event.turnId && candidate.turnId && event.turnId !== candidate.turnId) continue;
      candidates.push(candidate);
    }
    const terminalText = finalOutput;
    let matchedTerminalSuffix = false;
    for (const candidate of candidates) {
      const assistantText = safePayloadText(candidate.payload, "text").trim();
      if (slackDeliveryTextsCoalesce(assistantText, terminalText)) {
        terminalAssistantSequences.add(candidate.sequence);
        matchedTerminalSuffix = true;
      } else if (matchedTerminalSuffix) {
        // Coalesce only the contiguous terminal-shaped suffix. An earlier
        // distinct progress update in the same turn remains independently
        // deliverable even when a later SDK projection repeats the result.
        break;
      }
    }
  }
  const progressEvidence = orderedEvents.some((event) => event.type === "turn.completed")
    ? await listSlackInteractionProgressDeliveryEvidence(deps.db, {
        accountId: interaction.accountId,
        workspaceId: interaction.workspaceId,
        interactionId: interaction.id,
        sessionId: interaction.sessionId,
      })
    : [];
  // Slack delivery pages are chronological. This keeps pagination lossless and
  // ensures progress cannot appear after a terminal result.
  for (const event of orderedEvents) {
    lastSequence = Math.max(lastSequence, event.sequence);
    if (event.type === "agent.message.completed") {
      const latestAssistantText = safePayloadText(event.payload, "text");
      if (latestAssistantText && !terminalAssistantSequences.has(event.sequence)) {
        const progress = await claimSlackInteractionProgressDelivery(deps.db, {
          accountId: interaction.accountId,
          workspaceId: interaction.workspaceId,
          interactionId: interaction.id,
          claimHolderId,
          sessionEventSequence: event.sequence,
          maxProgress: MAX_PROGRESS_MESSAGES,
        });
        if (progress.kind === "not_owned") {
          throw new Error("Slack progress delivery lost its durable interaction claim");
        }
        if (progress.kind === "claimed") {
          await postDelivery(
            client,
            interaction,
            event,
            latestAssistantText,
            "progress",
            progress.delivery.operationId,
          );
        }
      }
    } else if (event.type === "session.requiresAction") {
      const card = await slackApprovalCard(
        deps,
        interaction,
        event,
        requester.mention,
        requester.authorized,
      );
      await postDelivery(
        client,
        interaction,
        event,
        card.text,
        "approval",
        card.operationId,
        card.blocks,
      );
    } else if (event.type === "session.humanInput.requested") {
      const card = await slackHumanInputCard(
        deps,
        interaction,
        event,
        requester.mention,
        requester.authorized,
      );
      if (card) {
        await postDelivery(
          client,
          interaction,
          event,
          card.text,
          "human-input",
          card.operationId,
          card.blocks,
        );
      }
    } else if (event.type === "system.update.pending") {
      const card = (await orchestrationNoticeEnabled("childRequiresAction"))
        ? await slackChildRequiresActionCard(deps, interaction, event, requester.mention)
        : null;
      if (card) await postUnsolicitedNotice(event, card.text, "child-blocked");
    } else if (event.type === "goal.paused") {
      const paused = (await orchestrationNoticeEnabled("goalPaused"))
        ? slackGoalPausedText(deps, interaction, event, requester.mention)
        : null;
      if (paused) await postUnsolicitedNotice(event, paused, "goal-paused");
    } else if (event.type === "turn.completed") {
      const payloadOutput = safePayloadText(event.payload, "output");
      const hasPublishableOutput = payloadOutput.trim().length > 0;
      if (safePayloadText(event.payload, "segmentLimit") === "budget_exhausted") {
        await postDelivery(
          client,
          interaction,
          event,
          `${requester.mention}OpenGeni reached a billing or usage limit. Ask your organization owner to check credits and usage limits, then reply in this thread to resume.`,
          "billing-limit",
        );
        terminal = "failed";
        continue;
      }
      // A completed turn is not necessarily a completed task. Input waits and
      // pacing yields settle without a result. Keep the cursor moving
      // and delivery open for the eventual response; never promote commentary
      // or invent a success message for these boundaries.
      if (!hasPublishableOutput || safePayloadText(event.payload, "segmentLimit")) {
        terminal = null;
        continue;
      }
      const output = payloadOutput;
      const normalizedOutput = output.trim();
      const existingProgress = progressEvidence.find(
        (delivery) =>
          slackDeliveryTextsCoalesce(boundedOutput(delivery.text).trim(), normalizedOutput) &&
          slackTerminalProgressSameTurn(event, delivery, interaction, terminalAssistantSequences),
      );
      if (existingProgress && normalizedOutput) {
        const publicationBlocks = hasPublishableOutput
          ? await slackSharedResultPublicationBlocks(
              deps,
              interaction,
              event,
              requester.authorized,
              existingProgress.operationId,
            )
          : [];
        // The assistant text may already have been accepted by Slack before a
        // replica observed turn.completed. Reconcile the same provider
        // operation id (including response-loss retries) instead of inventing
        // a second final post.
        await postDelivery(
          client,
          interaction,
          event,
          boundedOutput(existingProgress.text),
          "progress",
          existingProgress.operationId,
        );
        const posted = await getSlackBotPostOperation(
          deps.db,
          home.workspaceId,
          interaction.connectionId,
          existingProgress.operationId,
        );
        if (posted?.slackChannelId && posted.slackMessageTimestamp) {
          // The result is the result. Continuation prose and the recurring
          // action live on the control/Status card and `<command> info`.
          const text = boundedOutput(`${requester.mention}${existingProgress.text}`);
          // This rewrites a message that already carried the line, so it has to
          // carry it too, or the update would quietly strip it.
          const rendered = withWorkspaceLine(
            interaction.routedWorkspaceLabel,
            text,
            publicationBlocks.length > 0
              ? ([
                  { type: "section", text: { type: "mrkdwn", text } },
                  ...publicationBlocks,
                ] as SlackMessageBlock[])
              : undefined,
          );
          await client.updateMessage({
            operationId: deterministicUuid(
              slackPostSeed(
                interaction,
                `slack-terminal-update:${interaction.id}:${event.sequence}`,
              ),
            ),
            channelId: posted.slackChannelId,
            timestamp: posted.slackMessageTimestamp,
            text: rendered.text,
            ...(rendered.blocks
              ? {
                  blocks: rendered.blocks,
                }
              : {}),
          });
        }
      } else {
        const operationId = deterministicUuid(
          slackPostSeed(interaction, `slack-delivery:${interaction.id}:${event.sequence}:final`),
        );
        const text = boundedOutput(`${requester.mention}${output}`);
        const publicationBlocks = hasPublishableOutput
          ? await slackSharedResultPublicationBlocks(
              deps,
              interaction,
              event,
              requester.authorized,
              operationId,
            )
          : [];
        await postDelivery(
          client,
          interaction,
          event,
          text,
          "final",
          operationId,
          publicationBlocks.length > 0
            ? [{ type: "section", text: { type: "mrkdwn", text } }, ...publicationBlocks]
            : undefined,
        );
      }
      terminal = "completed";
    } else if (event.type === "turn.failed") {
      await postDelivery(
        client,
        interaction,
        event,
        `${requester.mention}OpenGeni could not complete this task. Reply in this thread to retry or ask for details.`,
        "failed",
      );
      terminal = "failed";
    } else if (event.type === "turn.cancelled") {
      await postDelivery(
        client,
        interaction,
        event,
        `${requester.mention}OpenGeni stopped this task.`,
        "cancelled",
      );
      terminal = "cancelled";
    }
  }
  if (terminal) {
    await closeSlackInteractionDelivery(deps.db, {
      ...interaction,
      claimHolderId,
      sequence: lastSequence,
      state: terminal,
    });
  } else {
    await advanceSlackInteractionDelivery(deps.db, {
      ...interaction,
      claimHolderId,
      sequence: lastSequence,
    });
    await releaseSlackInteractionDelivery(deps.db, {
      ...interaction,
      claimHolderId,
    });
  }
}

export function slackDeliveryTextsCoalesce(left: string, right: string): boolean {
  const normalizedLeft = left.trim();
  const normalizedRight = right.trim();
  if (!normalizedLeft || !normalizedRight) return false;
  if (normalizedLeft === normalizedRight) return true;
  const [shorter, longer] =
    normalizedLeft.length < normalizedRight.length
      ? [normalizedLeft, normalizedRight]
      : [normalizedRight, normalizedLeft];
  if (!longer.startsWith(shorter)) return false;
  const boundary = longer.slice(shorter.length, shorter.length + 1);
  return boundary.length === 0 || /[\s.,;:!?()[\]{}\-–—]/u.test(boundary);
}

function slackTerminalProgressSameTurn(
  event: SessionEvent,
  delivery: { turnId: string | null; sessionEventSequence: number },
  interaction: SlackInteraction,
  samePageTerminalSequences: ReadonlySet<number>,
): boolean {
  if (event.turnId && delivery.turnId) return event.turnId === delivery.turnId;
  if (event.turnId || delivery.turnId) return false;
  return (
    delivery.sessionEventSequence === interaction.lastDeliveredSessionEventSequence ||
    samePageTerminalSequences.has(delivery.sessionEventSequence)
  );
}

async function postDelivery(
  client: Awaited<ReturnType<typeof createOpenGeniSlackBotInteractionClient>>,
  interaction: SlackInteraction,
  event: SessionEvent,
  text: string,
  kind: string,
  operationId = deterministicUuid(
    slackPostSeed(interaction, `slack-delivery:${interaction.id}:${event.sequence}:${kind}`),
  ),
  blocks?: SlackMessageBlock[],
) {
  const rendered = withWorkspaceLine(interaction.routedWorkspaceLabel, boundedOutput(text), blocks);
  await client.postMessage({
    operationId,
    channelId: interaction.slackChannelId,
    threadTimestamp: interaction.slackThreadTs,
    text: rendered.text,
    ...(rendered.blocks ? { blocks: rendered.blocks } : {}),
  });
}

async function enqueueNormalizedSlackInteraction(
  deps: ApiRouteDeps,
  route: SlackInstallationRoute,
  entry: NormalizedSlackInteraction,
) {
  await enqueueSlackInteractionInbox(deps.db, {
    accountId: route.accountId,
    workspaceId: route.workspaceId,
    connectionId: route.connectionId,
    ...entry,
  });
}

async function readSignedSlackRequest(c: Context, deps: ApiRouteDeps) {
  const signingSecret = deps.settings.slackSigningSecret;
  if (!signingSecret)
    throw new HTTPException(503, {
      message: "Slack interactions are disabled",
    });
  const rawBytes = new Uint8Array(await c.req.raw.arrayBuffer());
  if (rawBytes.byteLength === 0 || rawBytes.byteLength > SLACK_INTERACTION_MAX_BODY_BYTES) {
    throw new HTTPException(rawBytes.byteLength > SLACK_INTERACTION_MAX_BODY_BYTES ? 413 : 400, {
      message: "invalid Slack request body",
    });
  }
  const rawBody = new TextDecoder("utf-8", { fatal: true }).decode(rawBytes);
  if (
    !verifySlackRequestSignature(
      {
        timestamp: c.req.header("x-slack-request-timestamp") ?? null,
        signature: c.req.header("x-slack-signature") ?? null,
        rawBody,
      },
      signingSecret,
    )
  ) {
    throw new HTTPException(401, { message: "invalid Slack signature" });
  }
  return { rawBody };
}

function normalizedFormInteraction(
  form: URLSearchParams,
  triggerKind: "slash_command",
): NormalizedSlackInteraction {
  const teamId = boundedString(form.get("team_id"), 64);
  const userId = boundedString(form.get("user_id"), 64);
  const channelId = boundedString(form.get("channel_id"), 64);
  const triggerId = boundedString(form.get("trigger_id"), 256);
  const text = boundedText(form.get("text"));
  if (!teamId || !userId || !channelId || !triggerId || !text) {
    throw new HTTPException(400, { message: "invalid Slack command payload" });
  }
  return {
    providerEventId: `command:${triggerId}`,
    providerMessageId: `command:${triggerId}`,
    slackTeamId: teamId,
    slackUserId: userId,
    slackChannelId: channelId,
    slackMessageTs: triggerId.slice(0, 64),
    slackThreadTs: null,
    triggerKind,
    text,
    hasFiles: false,
  };
}

const SLACK_BLOCK_ACTION_IDS = new Set([
  "opengeni.approval.approve",
  "opengeni.approval.reject",
  "opengeni.human_input.select",
  "opengeni.human_input.skip",
  "opengeni.session.status",
  "opengeni.session.pause",
  "opengeni.session.resume",
  "opengeni.shared_result.publish",
  "opengeni.route.select",
  "opengeni.route.cancel",
]);

export function normalizedBlockActionInteraction(
  payload: Record<string, unknown>,
): NormalizedSlackInteraction {
  const team = record(payload.team);
  const user = record(payload.user);
  const channel = record(payload.channel);
  const container = record(payload.container);
  const message = record(payload.message);
  const action = Array.isArray(payload.actions) ? record(payload.actions[0]) : null;
  const teamId = boundedString(team?.id, 64);
  const userId = boundedString(user?.id, 64);
  const channelId = boundedString(channel?.id, 64) ?? boundedString(container?.channel_id, 64);
  const messageTs = boundedString(message?.ts, 64) ?? boundedString(container?.message_ts, 64);
  const threadTs = boundedString(message?.thread_ts, 64);
  const actionTs = boundedString(action?.action_ts, 64) ?? boundedString(payload.action_ts, 64);
  const actionId = boundedString(action?.action_id, 128);
  const handleId = boundedString(action?.value, 64);
  if (
    !teamId ||
    !userId ||
    !channelId ||
    !messageTs ||
    !actionTs ||
    !actionId ||
    !SLACK_BLOCK_ACTION_IDS.has(actionId) ||
    !handleId ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(handleId) ||
    !Array.isArray(payload.actions) ||
    payload.actions.length !== 1
  ) {
    throw new HTTPException(400, { message: "invalid Slack block action" });
  }
  const identity = createHash("sha256")
    .update([teamId, userId, channelId, messageTs, actionTs, actionId, handleId].join("\n"))
    .digest("hex");
  return {
    providerEventId: `block:${identity}`,
    providerMessageId: `block:${identity}`,
    slackTeamId: teamId,
    slackUserId: userId,
    slackChannelId: channelId,
    slackMessageTs: messageTs,
    slackThreadTs: threadTs,
    triggerKind: "block_action",
    text: `${actionId}:${handleId}`,
    hasFiles: false,
  };
}

function humanInputResponse(questions: HumanInputQuestion[], text: string) {
  if (questions.length !== 1) return null;
  const question = questions[0]!;
  if (question.kind === "text") {
    return {
      outcome: "answered" as const,
      answers: [{ questionId: question.id, values: [text] }],
    };
  }
  const normalized = text.trim().toLowerCase();
  const matches = question.options.filter(
    (option) => option.id.toLowerCase() === normalized || option.label.toLowerCase() === normalized,
  );
  if (matches.length !== 1) {
    const other = text.trim();
    if (!other) return null;
    return {
      outcome: "answered" as const,
      answers: [{ questionId: question.id, values: [], other }],
    };
  }
  return {
    outcome: "answered" as const,
    answers: [{ questionId: question.id, values: [matches[0]!.id] }],
  };
}

function formatQuestions(questions: HumanInputQuestion[]) {
  return questions
    .slice(0, 5)
    .map((question, index) => {
      const options = question.options
        .slice(0, 10)
        .map((option) => option.label)
        .join(", ");
      return `${index + 1}. ${boundedOutput(question.prompt)}${
        options ? ` (${options}, or reply with another value)` : ""
      }`;
    })
    .join("\n");
}

export function isSlackInfoCommand(text: string): boolean {
  return text.trim().toLowerCase() === "info";
}

type SlackSlashResponse = {
  response_type: "ephemeral";
  text: string;
  unfurl_links: false;
  unfurl_media: false;
  blocks?: SlackMessageBlock[];
};

/**
 * The ephemeral `<command> info` card.
 *
 * It is a projection of what the caller can already do, not an action: no
 * session, no durable inbox row, no provider post. It re-proves the exact
 * Slack identity link plus live workspace grants before any
 * workspace-identifying text is echoed, so an unlinked or access-revoked
 * identity receives the ordinary connect view instead.
 */
async function slackInfoCommandResponse(
  deps: ApiRouteDeps,
  installation: SlackInstallationRoute,
  entry: NormalizedSlackInteraction,
): Promise<SlackSlashResponse> {
  const identity = {
    workspaceId: installation.workspaceId,
    connectionId: installation.connectionId,
    slackTeamId: entry.slackTeamId,
    slackUserId: entry.slackUserId,
  };
  const link = await getSlackBotUserLink(
    deps.db,
    installation.workspaceId,
    installation.connectionId,
    entry.slackUserId,
  );
  const grant = link
    ? await getWorkspaceGrant(deps.db, link.subjectId, installation.workspaceId, {
        principalKind: "human_session",
      })
    : null;
  if (
    !grant ||
    grant.accountId !== installation.accountId ||
    !hasPermission(grant.permissions, "sessions:read")
  ) {
    return ephemeralSlackResponse(
      link
        ? `Your Slack identity is linked, but it does not currently have access to this OpenGeni workspace. Request access: ${linkUrl(deps, identity)}. No session was created.`
        : `Link your Slack identity to OpenGeni before starting work: ${linkUrl(deps, identity)}. No session was created.`,
    );
  }
  const workspace = await getWorkspace(deps.db, installation.workspaceId);
  const command = deps.settings.slackCommand;
  const botMention = slackBotMention(installation.botUserId);
  // Every line is gated on the grant that actually authorizes it, so the card
  // stays a projection of what this caller can do rather than a generic manual.
  const canControl = hasPermission(grant.permissions, "sessions:control");
  const canCreate = hasPermission(grant.permissions, "sessions:create");
  const schedules = hasPermission(grant.permissions, "scheduled_tasks:manage")
    ? slackSchedulesUrl(deps, installation.workspaceId)
    : null;
  const workspaceUrl = slackWorkspaceUrl(deps, installation.workspaceId);
  const workspaceName = (workspace?.name ?? "").trim().slice(0, 120);
  const destination = workspaceName
    ? `the *${escapeSlackMrkdwn(workspaceName)}* workspace`
    : "your OpenGeni workspace";
  const lines = [
    "*Working with OpenGeni in Slack*",
    "",
    ...(canControl
      ? [
          "• *Continue a task:* reply in its Slack thread.",
          "• *Stop a task:* press *Stop* on its card, or reply `stop` in its thread.",
        ]
      : []),
    ...(canCreate
      ? [
          `• *Start a new task:* mention ${botMention} in a channel, run \`${command} <task>\`, or send a new top-level direct message.`,
        ]
      : []),
    ...(schedules
      ? [
          `• *Make a result recurring:* press *Status* on the task card and open *Make recurring*, or open <${schedules}|Schedules>.`,
        ]
      : []),
    `• *Where work lands:* ${destination}${workspaceUrl ? ` (<${workspaceUrl}|open OpenGeni>)` : ""}.`,
  ];
  const text = lines.join("\n");
  return ephemeralSlackResponse(text, [
    { type: "section", text: { type: "mrkdwn", text } },
  ] as SlackMessageBlock[]);
}

function ephemeralSlackResponse(text: string, blocks?: SlackMessageBlock[]): SlackSlashResponse {
  return {
    response_type: "ephemeral",
    text,
    unfurl_links: false,
    unfurl_media: false,
    ...(blocks ? { blocks } : {}),
  };
}

function slackBotMention(botUserId: string | null): string {
  return botUserId && /^[UWB][A-Z0-9]{1,63}$/.test(botUserId) ? `<@${botUserId}>` : "@OpenGeni";
}

function openSessionText(deps: ApiRouteDeps, workspaceId: string, sessionId: string) {
  const base = deps.settings.webBaseUrl ?? deps.settings.publicBaseUrl;
  if (!base) throw new Error("Slack session acknowledgement requires an absolute web base URL");
  const url = new URL(`/workspaces/${workspaceId}/sessions/${sessionId}`, base).toString();
  return `<${url}|Open in OpenGeni>`;
}

function slackWorkspaceUrl(deps: ApiRouteDeps, workspaceId: string): string | null {
  return safeSlackActionUrl(deps, `/workspaces/${encodeURIComponent(workspaceId)}`);
}

function slackSessionAuthorizationScopeKey(scope: SessionAuthorizationListScope | null): string {
  if (scope === null) return "standalone";
  if (scope.kind === "all") return "all";
  return JSON.stringify({
    kind: scope.kind,
    rootSessionIds: [...scope.rootSessionIds].sort(),
    sessionIds: [...scope.sessionIds].sort(),
  });
}

function slackSessionUrl(
  deps: ApiRouteDeps,
  workspaceId: string,
  sessionId: string,
): string | null {
  return safeSlackActionUrl(
    deps,
    `/workspaces/${encodeURIComponent(workspaceId)}/sessions/${encodeURIComponent(sessionId)}`,
  );
}

function safeSlackActionUrl(deps: ApiRouteDeps, pathname: string): string | null {
  const base = deps.settings.webBaseUrl ?? deps.settings.publicBaseUrl;
  if (!base) return null;
  try {
    const url = new URL(pathname, base);
    if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

function slackAppHomeLinkUrl(
  deps: ApiRouteDeps,
  installation: SlackInstallationRoute,
  slackTeamId: string,
  slackUserId: string,
): string | null {
  const signingSecret = deps.settings.slackSigningSecret;
  const base = slackWorkspaceUrl(deps, installation.workspaceId);
  if (!signingSecret || !base) return base;
  const url = new URL(
    `/workspaces/${encodeURIComponent(installation.workspaceId)}/capabilities`,
    base,
  );
  url.hash = new URLSearchParams({
    slack_link: createSlackUserLinkToken(signingSecret, {
      workspaceId: installation.workspaceId,
      connectionId: installation.connectionId,
      slackTeamId,
      slackUserId,
    }),
  }).toString();
  return url.toString();
}

function makeRecurringText(
  deps: ApiRouteDeps,
  workspaceId: string,
  sessionId: string,
): string | null {
  // Unchanged deep-link contract: the schedules editor plus the exact source
  // session UUID, and nothing copied out of Slack. A deployment without an
  // absolute web base URL simply omits the action instead of failing a card.
  const base = slackSchedulesUrl(deps, workspaceId);
  if (!base) return null;
  const url = new URL(base);
  url.searchParams.set("sourceSessionId", sessionId);
  return `<${url.toString()}|Make recurring>`;
}

function slackSchedulesUrl(deps: ApiRouteDeps, workspaceId: string): string | null {
  return safeSlackActionUrl(deps, `/workspaces/${encodeURIComponent(workspaceId)}/schedules`);
}

function linkUrl(
  deps: ApiRouteDeps,
  entry: Pick<
    SlackInteractionInboxEntry,
    "workspaceId" | "connectionId" | "slackTeamId" | "slackUserId"
  >,
  /**
   * When this URL goes into a message posted under a deterministic operation
   * id, the token has to be deterministic too. The post ledger binds that id to
   * a digest over the text, so a retry that mints a fresh token renders
   * different bytes and is refused as a conflicting request, permanently. Pass
   * the originating inbox row's creation time: the token is then stable for that
   * row and still expires fifteen minutes after the message could first have
   * been read.
   */
  mintedAtMs?: number,
) {
  const base = deps.settings.webBaseUrl ?? deps.settings.publicBaseUrl;
  const signingSecret = deps.settings.slackSigningSecret;
  if (!base || !signingSecret) return "OpenGeni Settings → Integrations → Slack";
  const url = new URL(`/workspaces/${entry.workspaceId}/capabilities`, base);
  // Fragments stay out of HTTP request lines, reverse-proxy logs, Referer
  // headers, and managed-auth callback URLs. Query-form bearers are rejected.
  url.hash = new URLSearchParams({
    slack_link: createSlackUserLinkToken(signingSecret, entry, mintedAtMs),
  }).toString();
  return url.toString();
}

async function requireManagedSlackLinkHuman(c: Context, deps: ApiRouteDeps) {
  if (c.req.header("authorization")) {
    throw new HTTPException(401, { message: "managed browser sign-in required" });
  }
  const context = await requireAccessContext(c, deps);
  if (context.mode !== "managed" || !context.subjectId.startsWith("user:")) {
    throw new HTTPException(403, { message: "managed browser sign-in required" });
  }
  return context;
}

function freshSlackLinkRequired() {
  return new HTTPException(400, {
    message: "This Slack link is invalid or expired. Request a fresh link from Slack.",
  });
}

function slackLinkAccessHttpError(error: unknown): HTTPException {
  if (error instanceof HTTPException) return error;
  if (!(error instanceof SlackUserLinkAccessPersistenceError)) throw error;
  if (error.code === "version_conflict" || error.code === "idempotency_conflict") {
    return new HTTPException(409, {
      message: "The Slack access request changed. Refresh and try again.",
    });
  }
  if (error.code === "state_conflict") {
    return new HTTPException(409, {
      message: "This Slack link is no longer pending. Request a fresh link from Slack.",
    });
  }
  return freshSlackLinkRequired();
}

type SlackUserLinkToken = {
  workspaceId: string;
  connectionId: string;
  slackTeamId: string;
  slackUserId: string;
  expiresAt: number;
};

export function createSlackUserLinkToken(
  signingSecret: string,
  entry: Pick<
    SlackInteractionInboxEntry,
    "workspaceId" | "connectionId" | "slackTeamId" | "slackUserId"
  >,
  nowMs = Date.now(),
) {
  const payload = Buffer.from(
    JSON.stringify({
      workspaceId: entry.workspaceId,
      connectionId: entry.connectionId,
      slackTeamId: entry.slackTeamId,
      slackUserId: entry.slackUserId,
      expiresAt: nowMs + SLACK_USER_LINK_TTL_MS,
    } satisfies SlackUserLinkToken),
    "utf8",
  ).toString("base64url");
  const signature = createHmac("sha256", signingSecret).update(payload).digest("base64url");
  return `${payload}.${signature}`;
}

export function verifySlackUserLinkToken(
  signingSecret: string,
  token: string,
  nowMs = Date.now(),
): SlackUserLinkToken | null {
  const [payload, signature, extra] = token.split(".");
  if (!payload || !signature || extra || payload.length > 1_500 || signature.length > 128)
    return null;
  const expected = createHmac("sha256", signingSecret).update(payload).digest("base64url");
  const actualBytes = Buffer.from(signature, "utf8");
  const expectedBytes = Buffer.from(expected, "utf8");
  if (actualBytes.length !== expectedBytes.length || !timingSafeEqual(actualBytes, expectedBytes)) {
    return null;
  }
  try {
    const value = record(JSON.parse(Buffer.from(payload, "base64url").toString("utf8")));
    const workspaceId = boundedString(value?.workspaceId, 64);
    const connectionId = boundedString(value?.connectionId, 64);
    const slackTeamId = boundedString(value?.slackTeamId, 64);
    const slackUserId = boundedString(value?.slackUserId, 64);
    const expiresAt = value?.expiresAt;
    if (
      !workspaceId ||
      !connectionId ||
      !slackTeamId ||
      !slackUserId ||
      typeof expiresAt !== "number" ||
      !Number.isSafeInteger(expiresAt) ||
      expiresAt < nowMs ||
      expiresAt > nowMs + SLACK_USER_LINK_TTL_MS
    ) {
      return null;
    }
    return { workspaceId, connectionId, slackTeamId, slackUserId, expiresAt };
  } catch {
    return null;
  }
}

function slackRouteKey(channelId: string, threadTs: string) {
  return `${channelId}:${threadTs}`;
}

function isDirectMessageShortcut(
  entry: Pick<SlackInteractionInboxEntry, "triggerKind" | "slackChannelId">,
) {
  return entry.triggerKind === "message_shortcut" && entry.slackChannelId.startsWith("D");
}

function usesPrivateBotDm(
  interaction: Pick<SlackInteraction, "visibility">,
  entry: Pick<SlackInteractionInboxEntry, "triggerKind" | "slackChannelId">,
): boolean {
  return (
    isDirectMessageShortcut(entry) ||
    (interaction.visibility === "private" && entry.triggerKind !== "dm")
  );
}

function deterministicUuid(value: string) {
  const bytes = createHash("sha256").update(value).digest().subarray(0, 16);
  bytes[6] = (bytes[6]! & 0x0f) | 0x50;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = bytes.toString("hex");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function parseJsonObject(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value);
    const result = record(parsed);
    if (result) return result;
  } catch {
    // normalized below
  }
  throw new HTTPException(400, { message: "invalid Slack JSON payload" });
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function boundedString(value: unknown, max: number): string | null {
  return typeof value === "string" && value.length > 0 && Buffer.byteLength(value) <= max
    ? value
    : null;
}

function boundedText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_SLACK_INPUT_CHARS);
}

/** Single-line, character-bounded preview for a blocked-worker pointer card. */
function boundedSlackChildDetail(value: string) {
  const collapsed = value.replace(/\s+/gu, " ").trim();
  return collapsed.length <= MAX_SLACK_CHILD_DETAIL_CHARS
    ? collapsed
    : `${collapsed.slice(0, MAX_SLACK_CHILD_DETAIL_CHARS - 1)}…`;
}

function boundedOutput(value: string, limit = MAX_SLACK_TEXT_CHARS) {
  return value.length <= limit ? value : `${value.slice(0, limit - 20)}\n… output truncated`;
}

function safePayloadText(payload: unknown, field: string) {
  const value = record(payload)?.[field];
  return typeof value === "string" ? boundedOutput(value) : "";
}

function safeErrorCode(error: unknown) {
  if (error instanceof SlackBotProviderError) return error.code.slice(0, 128);
  if (error instanceof SlackInteractionPermanentError) return error.code.slice(0, 128);
  if (error instanceof SlackInteractionRetryableError) return error.code.slice(0, 128);
  if (error instanceof HTTPException) return `http_${error.status}`;
  const raw = error instanceof Error ? error.name : "slack_interaction_error";
  return (
    raw
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "_")
      .slice(0, 128) || "error"
  );
}

function slackAdmissionFailureText(error: HTTPException) {
  if (error.status === 402) {
    return "OpenGeni could not start this task because the selected model has no available billing source. Open OpenGeni, select a connected subscription model, and try again.";
  }
  if (error.status === 429) {
    return "OpenGeni could not start this task because this workspace has reached a usage limit. Try again later or review the workspace limits in OpenGeni.";
  }
  return "OpenGeni could not start this task because the workspace rejected the session settings. Open OpenGeni, select an available model, and try again.";
}

class SlackInteractionPermanentError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "SlackInteractionPermanentError";
  }
}

class SlackInteractionRetryableError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "SlackInteractionRetryableError";
  }
}

function permanentSlackInteractionError(error: unknown) {
  return error instanceof SlackInteractionPermanentError || error instanceof HTTPException;
}

const PERMANENT_SLACK_DELIVERY_CODES = new Set([
  "account_inactive",
  "cannot_reply_to_message",
  "channel_not_found",
  "invalid_auth",
  "invalid_ts",
  "is_archived",
  "message_not_found",
  "not_authed",
  "not_in_channel",
  "reaction_checkpoint_invalid",
  "reaction_checkpoint_too_large",
  "reaction_pagination_exhausted",
  "reaction_pagination_invalid",
  "slack_connect_unsupported",
  "token_expired",
  "token_revoked",
]);

function permanentSlackDeliveryError(error: unknown) {
  if (!(error instanceof SlackBotProviderError)) return false;
  if (PERMANENT_SLACK_DELIVERY_CODES.has(error.code)) return true;
  const status = /^http_(\d{3})$/.exec(error.code)?.[1];
  return status
    ? Number(status) >= 400 && Number(status) < 500 && status !== "408" && status !== "429"
    : false;
}

function slackDeliveryRetryMs(error: unknown, attemptCount: number) {
  if (error instanceof SlackBotProviderError && error.retryAfterMs) {
    return error.retryAfterMs;
  }
  return Math.min(1_000 * 2 ** Math.max(0, attemptCount - 1), MAX_DELIVERY_RETRY_MS);
}

function slackDeliveryErrorCode(error: unknown) {
  if (error instanceof SlackBotProviderError) return error.code.slice(0, 128);
  return safeErrorCode(error);
}
